import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import FormSection from '../FormSection';
import FormField from '../FormField';
import Input from '../Input';
import SearchableSelect from '../SearchableSelect';
import Textarea from '../Textarea';
import MultiSelectSpeakers from '../MultiSelectSpeakers';
import ImageModal from '../ImageModal';
import Modal from '../Modal';
import CustomSelect from '../CustomSelect';
import { ParallelEvent, Speaker, AttendeeProfile, ThematicAxis } from '../../types/profile.types';
import { thematicAxes, subejesPorEje, attendeeProfilesByThematicAxis, countries } from '../../data/profileData';
import MultiSelectTags from '../MultiSelectTags';
import { validateFileSize, validateMultipleFiles, MAX_FILE_SIZE_IMAGES, MAX_FILE_SIZE_DOCUMENTS } from '../../utils/fileValidation';
import { useFileError } from '../../contexts/FileErrorContext';
import { useUploadProgress } from '../../contexts/UploadProgressContext';
import { useTimeout } from '../../contexts/TimeoutContext';
import { uploadToSupabase } from '../../services/supabase';
import { handleRateLimitError } from '../../utils/rateLimitHandler';
import { handleUploadError } from '../../utils/uploadErrorHandler';
import { CheckCircleIcon, XCircleIcon, AlertTriangleIcon } from '../icons/AdminIcons';
import '../CheckboxGroup.css';
import './ParallelEventsSection.css';
import './ThematicAxesSection.css';
import './SpeakersSection.css';

// Helper function para obtener el estado del evento (igual que en AdminPanel)
const getEventStatusHelper = (event: any): { completionStatus: 'complete' | 'incomplete', hasRequestApproval: boolean } => {
  if (!event) return { completionStatus: 'incomplete', hasRequestApproval: false };
  
  const hasName = event.name && event.name.toString().trim() !== '';
  const hasDescription = event.description && event.description.toString().trim() !== '';
  const hasSpeakers = (event.speakerIds && event.speakerIds.length > 0) || 
                    (event.localSpeakers && event.localSpeakers.length > 0);
  const hasDuration = event.duration && event.duration.toString().trim() !== '';
  const hasThematicAxes = event.thematicAxes && Array.isArray(event.thematicAxes) && event.thematicAxes.length > 0;
  const hasAttendeeProfiles = event.attendeeProfiles && Array.isArray(event.attendeeProfiles) && event.attendeeProfiles.length > 0;
  const isComplete = hasName && hasDescription && hasSpeakers && hasDuration && hasThematicAxes && hasAttendeeProfiles;
  
  return {
    completionStatus: isComplete ? 'complete' : 'incomplete',
    hasRequestApproval: event.requestApproval === true
  };
};

interface ParallelEventsSectionProps {
  parallelEvents: ParallelEvent[];
  speakers: Speaker[];
  onChange: (events: ParallelEvent[]) => void;
  onAddSpeaker?: (speaker: Speaker) => void; // Función para agregar orador a la lista principal
  readOnly?: boolean;
  hideTitle?: boolean;
  initialExpandedEventId?: string;
  showApprovalBadge?: boolean;
  onApprovalStatusChange?: (eventId: string, status: 'pending' | 'approved' | 'rejected') => void;
  userId?: number;
}

interface ParallelEventCardProps {
  event: ParallelEvent;
  speakers: Speaker[];
  onUpdate: (id: string, field: keyof ParallelEvent, value: any) => void;
  onRemove: (id: string) => void;
  onAddSpeaker?: (speaker: Speaker) => void; // Función para agregar orador a la lista principal
  readOnly?: boolean;
  initialExpanded?: boolean;
  newLocalSpeakerIds?: Set<string>; // IDs de oradores locales recién creados
  onNewLocalSpeaker?: (speakerId: string) => void; // Callback para notificar sobre nuevo orador local
  showApprovalBadge?: boolean;
  onApprovalStatusChange?: (eventId: string, status: 'pending' | 'approved' | 'rejected') => void;
  userId?: number;
}

interface LocalSpeakerCardProps {
  speaker: Speaker;
  onUpdate: (id: string, field: keyof Speaker, value: any) => void;
  onUpdateWithBlur?: (id: string, field: keyof Speaker, value: any) => void;
  onRemove: (id: string) => void;
  readOnly?: boolean;
  initialExpanded?: boolean;
  newLocalSpeakerIds?: Set<string>; // IDs de oradores locales recién creados
}

// Función para capitalizar solo la primera letra (mantener el resto como está, incluyendo espacios)
const capitalizeFirstLetter = (str: string): string => {
  if (!str) return '';
  // Encontrar el primer carácter que no sea espacio
  const firstNonSpaceIndex = str.search(/\S/);
  if (firstNonSpaceIndex === -1) {
    // Si solo hay espacios, retornar como está
    return str;
  }
  // Capitalizar solo la primera letra no-espacio, mantener todo lo demás (incluyendo espacios)
  return str.slice(0, firstNonSpaceIndex) + 
         str.charAt(firstNonSpaceIndex).toUpperCase() + 
         str.slice(firstNonSpaceIndex + 1);
};

// Componente de tarjeta para oradores locales (similar a SpeakerCard de SpeakersSection)
const LocalSpeakerCard: React.FC<LocalSpeakerCardProps> = ({ speaker, onUpdate, onUpdateWithBlur, onRemove, readOnly = false, initialExpanded = false, newLocalSpeakerIds = new Set() }) => {
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [organizationLogoPreview, setOrganizationLogoPreview] = useState<string | null>(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showOrganizationLogoModal, setShowOrganizationLogoModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const isNewSpeaker = newLocalSpeakerIds.has(speaker.id);
  
  // Inicializar como false por defecto, luego se actualizará en useLayoutEffect
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const hasBeenManuallyToggledRef = useRef<boolean>(false);
  const hasInitializedRef = useRef<boolean>(false);
  
  // useLayoutEffect se ejecuta sincrónicamente después de todas las mutaciones del DOM
  // pero antes de que el navegador pinte la pantalla, asegurando que el estado se establezca antes del render
  useLayoutEffect(() => {
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      const shouldBeExpanded = initialExpanded || newLocalSpeakerIds.has(speaker.id);

      setIsExpanded(shouldBeExpanded);
    }
  }, []); // Solo ejecutar una vez después del montaje
  
  // Sincronizar isExpanded cuando initialExpanded o newLocalSpeakerIds cambian (importante para nuevos oradores)
  // Esto asegura que si el prop cambia después del montaje inicial, el estado se actualice
  useEffect(() => {
    // Si el usuario no ha cambiado manualmente el estado y ya se inicializó, actualizar según los props
    if (!hasBeenManuallyToggledRef.current && hasInitializedRef.current) {
      const shouldBeExpanded = initialExpanded || newLocalSpeakerIds.has(speaker.id);

      // Solo actualizar si el estado actual es diferente al deseado
      if (shouldBeExpanded !== isExpanded) {

        setIsExpanded(shouldBeExpanded);
      }
    }
  }, [initialExpanded, newLocalSpeakerIds, speaker.id, isExpanded]);
  const photoFileInputRef = useRef<HTMLInputElement>(null);
  const organizationLogoFileInputRef = useRef<HTMLInputElement>(null);
  const { showFileSizeError } = useFileError();
  const { startUpload, updateProgress, finishUpload } = useUploadProgress();
  const { showTimeout } = useTimeout();
  
  // Estado local para campos de texto para evitar re-renders mientras se escribe
  const [localFirstName, setLocalFirstName] = useState(speaker.firstName || '');
  const [localLastName, setLocalLastName] = useState(speaker.lastName || '');
  const isTypingRef = useRef(false);
  const [urlErrors, setUrlErrors] = useState<{ linkedin: string; instagram: string; twitter: string }>({
    linkedin: '',
    instagram: '',
    twitter: ''
  });
  const [biographyError, setBiographyError] = useState<string>('');

  // Validar URLs y biografía continuamente para que los errores persistan mientras haya contenido inválido
  useEffect(() => {
    // Validar biografía - SOLO validar URL, NO mostrar mensaje de obligatorio
    // La validación de obligatorio se hace en el formulario completo, pero sin mostrar mensaje aquí
    const biography = speaker.biography || '';
    // La validación de URL es independiente de LinkedIn
    if (biography.trim() === '') {
      setBiographyError('');
    } else if (!validateUrl(biography)) {
      setBiographyError('Por favor, ingresa una URL válida');
    } else {
      setBiographyError('');
    }

    // Validar URLs de redes sociales
    const newUrlErrors = { ...urlErrors };
    if (speaker.linkedin && speaker.linkedin.trim() && !validateUrl(speaker.linkedin)) {
      newUrlErrors.linkedin = 'Por favor, ingresa una URL válida';
    } else {
      newUrlErrors.linkedin = '';
    }
    if (speaker.instagram && speaker.instagram.trim() && !validateUrl(speaker.instagram)) {
      newUrlErrors.instagram = 'Por favor, ingresa una URL válida';
    } else {
      newUrlErrors.instagram = '';
    }
    if (speaker.twitter && speaker.twitter.trim() && !validateUrl(speaker.twitter)) {
      newUrlErrors.twitter = 'Por favor, ingresa una URL válida';
    } else {
      newUrlErrors.twitter = '';
    }
    setUrlErrors(newUrlErrors);
  }, [speaker.biography, speaker.instagram, speaker.twitter, speaker.linkedin]);

  // Función de validación de URL (igual al form participar)
  const validateUrl = (url: string): boolean => {
    if (url.trim() === '') return true; // Campo opcional
    
    // Si ya tiene protocolo, validar URL completa
    if (url.startsWith('http://') || url.startsWith('https://')) {
      const urlRegex = /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
      return urlRegex.test(url);
    }
    
    // Si no tiene protocolo, validar solo dominio
    const domainRegex = /^(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
    return domainRegex.test(url);
  };

  // Validar Biografía: obligatorio PERO puede estar vacío si LinkedIn está lleno
  const validateBiography = (biography: string, linkedin: string): boolean => {
    const biographyTrimmed = biography.trim();
    const linkedinTrimmed = linkedin.trim();
    
    // Si LinkedIn está lleno, Biografía puede estar vacío
    if (linkedinTrimmed !== '') {
      return true;
    }
    
    // Si LinkedIn está vacío, Biografía es obligatorio
    return biographyTrimmed !== '';
  };
  
  // Sincronizar estado local cuando cambia el prop speaker (solo si no estamos escribiendo)
  useEffect(() => {
    if (!isTypingRef.current) {
      setLocalFirstName(speaker.firstName || '');
      setLocalLastName(speaker.lastName || '');
    }
  }, [speaker.firstName, speaker.lastName]);

  useEffect(() => {
    try {
      if (speaker.photo && typeof speaker.photo !== 'string') {
        const reader = new FileReader();
        reader.onloadend = () => setPhotoPreview(reader.result as string);
        reader.onerror = () => setPhotoPreview(null);
        reader.readAsDataURL(speaker.photo);
      } else if (typeof speaker.photo === 'string' && speaker.photo) {
        // Aceptar: data:image/, http://, https://, o /storage/files/ (proxy URL)
        if (speaker.photo.startsWith('data:image/') || 
            speaker.photo.startsWith('http://') || 
            speaker.photo.startsWith('https://') ||
            speaker.photo.startsWith('/storage/files/')) {
          setPhotoPreview(speaker.photo);
        } else {
          setPhotoPreview(null);
        }
      } else {
        setPhotoPreview(null);
      }
    } catch (error) {
      setPhotoPreview(null);
    }
  }, [speaker.photo]);

  useEffect(() => {
    try {
      if (speaker.organizationLogo && typeof speaker.organizationLogo !== 'string') {
        const reader = new FileReader();
        reader.onloadend = () => {
          setOrganizationLogoPreview(reader.result as string);
        };
        reader.onerror = () => {
          setOrganizationLogoPreview(null);
        };
        reader.readAsDataURL(speaker.organizationLogo);
      } else if (typeof speaker.organizationLogo === 'string' && speaker.organizationLogo) {
        // Validar que sea una URL válida o base64 válido
        // Aceptar: data:image/, http://, https://, o /storage/files/ (proxy URL)
        if (speaker.organizationLogo.startsWith('data:image/') || 
            speaker.organizationLogo.startsWith('http://') || 
            speaker.organizationLogo.startsWith('https://') ||
            speaker.organizationLogo.startsWith('/storage/files/')) {
          setOrganizationLogoPreview(speaker.organizationLogo);
        } else {
          setOrganizationLogoPreview(null);
        }
      } else {
        setOrganizationLogoPreview(null);
      }
    } catch (error) {
      setOrganizationLogoPreview(null);
    }
  }, [speaker.organizationLogo]);

  const handlePhotoChange = async (file: File) => {
    // Validar tamaño de foto antes de agregarla
    const validation = validateFileSize(file, MAX_FILE_SIZE_IMAGES, 'image');
    if (!validation.valid) {
      showFileSizeError(validation.error || 'La foto excede el tamaño máximo permitido.');
      return;
    }
    
    try {
      // Mostrar preview inmediato
      const reader = new FileReader();
      reader.onloadend = () => {
        const previewUrl = reader.result as string;
        setPhotoPreview(previewUrl);
        // Si el modal está abierto, mantenerlo abierto con la nueva imagen
        if (showImageModal) {
          // El modal se actualizará automáticamente porque imageUrl cambia
        }
      };
      reader.readAsDataURL(file);
      
      // Iniciar progreso de subida
      startUpload(file.name, 1);
      
      // Subir inmediatamente a Supabase
      const photoUrl = await uploadToSupabase(file, {
        folder: 'perfil-institucional/parallel-events/local-speakers/photos',
        onProgress: (progress) => {
          updateProgress(progress, 0);
        }
      });
      
      // Finalizar progreso
      finishUpload();
      
      // Log para debugging
      
      // Actualizar con la URL de Supabase
      onUpdate(speaker.id, 'photo', photoUrl);
      
      // Forzar actualización del preview inmediatamente
      if (photoUrl) {
        setPhotoPreview(photoUrl);
      }
    } catch (error: any) {
      // Verificar si es un error de rate limiting (429)
      if (handleRateLimitError(error)) {
        finishUpload();
        return;
      }
      
      // Verificar si es un error de formato o tamaño del backend
      if (handleUploadError(error, showFileSizeError)) {
        finishUpload();
        return;
      }
      
      // Verificar si es un error de timeout
      if (error?.isTimeout) {
        showTimeout(error.operationName || 'la subida de la foto');
        finishUpload();
        return;
      }
      
      // Finalizar progreso incluso si hay error
      finishUpload();
      // Error silencioso, no se actualiza el estado
    }
  };

  const handlePhotoChangeClick = () => {
    photoFileInputRef.current?.click();
  };

  const handlePhotoDelete = async () => {
    // Limpiar el preview
    setPhotoPreview(null);
    // Actualizar el orador sin foto
    onUpdate(speaker.id, 'photo', undefined);
  };

  const handleOrganizationLogoChange = async (file: File) => {
    // Validar tipo de archivo - solo PNG y formatos vectoriales editables (sin PDF)
    const allowedTypes = [
      'image/png',
      'image/svg+xml',
      'image/svg',
      'application/postscript',
      'application/illustrator'
    ];
    const allowedExtensions = ['.png', '.svg', '.ai', '.eps'];

    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    const isValidType = allowedTypes.includes(file.type) || 
                        allowedExtensions.includes(fileExtension) ||
                        file.name.toLowerCase().endsWith('.svg') ||
                        file.name.toLowerCase().endsWith('.ai') ||
                        file.name.toLowerCase().endsWith('.eps');

    if (!isValidType) {
      showFileSizeError('El logo debe estar en formato PNG o formato de curvas editable (SVG, AI, EPS).', true);
      return;
    }

    // Validar tamaño de logo antes de agregarlo
    const validation = validateFileSize(file, MAX_FILE_SIZE_IMAGES, 'image');
    if (!validation.valid) {
      showFileSizeError(validation.error || 'El logo excede el tamaño máximo permitido.');
      return;
    }
    
    try {
      // Mostrar preview inmediato
      const reader = new FileReader();
      reader.onloadend = () => {
        const previewUrl = reader.result as string;
        setOrganizationLogoPreview(previewUrl);
        // Si el modal está abierto, mantenerlo abierto con la nueva imagen
        if (showOrganizationLogoModal) {
          // El modal se actualizará automáticamente porque imageUrl cambia
        }
      };
      reader.readAsDataURL(file);
      
      // Iniciar progreso de subida
      startUpload(file.name, 1);
      
      // Subir inmediatamente a Supabase
      const logoUrl = await uploadToSupabase(file, {
        folder: 'perfil-institucional/parallel-events/local-speakers/organization-logos',
        onProgress: (progress) => {
          updateProgress(progress, 0);
        }
      });
      
      // Finalizar progreso
      finishUpload();
      
      // Log para debugging
      
      // Actualizar con la URL de Supabase
      onUpdate(speaker.id, 'organizationLogo', logoUrl);
      
      // Forzar actualización del preview inmediatamente
      if (logoUrl) {
        setOrganizationLogoPreview(logoUrl);
      }
    } catch (error: any) {
      // Verificar si es un error de rate limiting (429)
      if (handleRateLimitError(error)) {
        finishUpload();
        return;
      }
      
      // Verificar si es un error de formato o tamaño del backend
      if (handleUploadError(error, showFileSizeError)) {
        finishUpload();
        return;
      }
      
      // Verificar si es un error de timeout
      if (error?.isTimeout) {
        showTimeout(error.operationName || 'la subida del logo de la organización');
        finishUpload();
        return;
      }
      
      // Finalizar progreso incluso si hay error
      finishUpload();
      // Error silencioso para otros tipos de error
    }
  };

  const handleOrganizationLogoChangeClick = () => {
    organizationLogoFileInputRef.current?.click();
  };

  const handleOrganizationLogoDelete = async () => {
    // Limpiar el preview
    setOrganizationLogoPreview(null);
    // Actualizar el orador sin logo
    onUpdate(speaker.id, 'organizationLogo', undefined);
  };

  const handleBlur = (field: keyof Speaker, value: string) => {
    if (field === 'firstName' || field === 'lastName' || field === 'nationality' || 
        field === 'organization' || field === 'position') {
      const capitalized = capitalizeFirstLetter(value);
      if (capitalized !== value) {
        onUpdate(speaker.id, field, capitalized);
      }
    }
  };

  return (
    <div className="local-speaker-card">
      <div className="local-speaker-header">
        <h3 className="local-speaker-title">
          {localFirstName || localLastName ? `${localFirstName} ${localLastName}`.trim() || 'Nuevo Orador' : 'Nuevo Orador'}
        </h3>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            type="button"
            className="minimize-button"
            onClick={() => {
              hasBeenManuallyToggledRef.current = true;
              setIsExpanded(!isExpanded);
            }}
            title={isExpanded ? 'Minimizar' : 'Expandir'}
            style={{
              background: 'transparent',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              padding: '6px 12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#64748b',
              fontSize: '14px',
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 500
            }}
          >
            {isExpanded ? '−' : '+'}
          </button>
          {!readOnly && (
            <button 
              type="button" 
              className="remove-button"
              onClick={() => setShowDeleteModal(true)}
            >
              Eliminar
            </button>
          )}
        </div>
      </div>
      {isExpanded && (
      <div>
        <div className="form-row">
          <FormField label="Foto" required>
            <div className="photo-upload-container">
              <div className="photo-preview">
                {photoPreview ? (
                  <img 
                    src={photoPreview} 
                    alt="Foto del orador" 
                    className="photo-preview-image"
                    onClick={() => setShowImageModal(true)}
                    style={{ cursor: 'pointer' }}
                  />
                ) : (
                  <div className="photo-placeholder"><p>Sin foto</p></div>
                )}
              </div>
              {photoPreview && (
                <ImageModal
                  isOpen={showImageModal}
                  imageUrl={photoPreview}
                  alt="Foto del orador"
                  onClose={() => setShowImageModal(false)}
                  onSave={async (editedImageUrl) => {
                    // Convertir la URL del blob a File
                    try {
                      // La URL puede ser blob, data, o una URL de WordPress
                      // Si es una URL de WordPress, necesitamos convertirla a blob primero
                      if (!editedImageUrl) {

                        return;
                      }

                      let blob: Blob;
                      
                      if (editedImageUrl.startsWith('blob:') || editedImageUrl.startsWith('data:')) {
                        // Es un blob o data URL, hacer fetch directamente
                        const response = await fetch(editedImageUrl);
                        if (!response.ok) {
                          throw new Error(`Error al cargar la imagen: ${response.status} ${response.statusText}`);
                        }
                        blob = await response.blob();
                      } else {
                        // Es una URL de WordPress, usar el proxy para evitar CORS
                        let proxyBaseUrl = process.env.REACT_APP_API_URL || window.location.origin;
                        proxyBaseUrl = proxyBaseUrl.replace(/\/api\/?$/, '');
                        const proxyUrl = `${proxyBaseUrl}/api/proxy-image?url=${encodeURIComponent(editedImageUrl)}`;
                        const response = await fetch(proxyUrl);
                        if (!response.ok) {
                          throw new Error(`Error al cargar la imagen: ${response.status} ${response.statusText}`);
                        }
                        blob = await response.blob();
                      }

                      // Extraer el nombre original del archivo de la URL original
                      const getOriginalFileName = (url: string): string => {
                        try {
                          // Si es una URL de WordPress, extraer el nombre del archivo
                          if (url && url.includes('/')) {
                            const urlParts = url.split('/');
                            const fileName = urlParts[urlParts.length - 1];
                            // Remover parámetros de query si existen
                            const fileNameWithoutParams = fileName.split('?')[0];
                            // Si tiene nombre válido, usarlo
                            if (fileNameWithoutParams && fileNameWithoutParams.includes('.')) {
                              return fileNameWithoutParams;
                            }
                          }
                          // Si es un blob o data URL, usar nombre por defecto
                          return 'photo.jpg';
                        } catch {
                          return 'photo.jpg';
                        }
                      };

                      const originalFileName = getOriginalFileName(photoPreview || '');
                      // Extraer nombre y extensión
                      const lastDotIndex = originalFileName.lastIndexOf('.');
                      let fileNameWithoutExt = originalFileName;
                      let fileExtension = '.jpg';
                      
                      if (lastDotIndex > 0) {
                        fileNameWithoutExt = originalFileName.substring(0, lastDotIndex);
                        fileExtension = originalFileName.substring(lastDotIndex);
                      }
                      
                      // Agregar "-edited" antes de la extensión
                      const editedFileName = `${fileNameWithoutExt}-edited${fileExtension}`;

                      const file = new File([blob], editedFileName, { type: blob.type || 'image/jpeg' });
                      
                      // Actualizar el preview
                      setPhotoPreview(editedImageUrl);
                      
                      // Subir la imagen editada
                      await handlePhotoChange(file);
                    } catch (error) {

                      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';

                      // Error silencioso - no mostrar diálogo al usuario
                    }
                  }}
                  allowEdit={!readOnly}
                  isPhoto={true}
                  onPhotoChange={handlePhotoChangeClick}
                  onPhotoDelete={handlePhotoDelete}
                />
              )}
              {!readOnly && (
                <>
                  <button 
                    className="photo-upload-btn"
                    onClick={() => {
                      if (photoPreview) {
                        setShowImageModal(true);
                      } else {
                        photoFileInputRef.current?.click();
                      }
                    }}
                  >
                    {photoPreview ? 'Editar Foto' : 'Subir Foto'}
                  </button>
                  <input
                    ref={photoFileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        // Validar tamaño de foto antes de agregarla
                        const validation = validateFileSize(file, MAX_FILE_SIZE_IMAGES, 'image');
                        if (!validation.valid) {
                          showFileSizeError(validation.error || 'La foto excede el tamaño máximo permitido.');
                          e.target.value = '';
                          return;
                        }
                        handlePhotoChange(file);
                      }
                      // Resetear el input
                      if (e.target) {
                        e.target.value = '';
                      }
                    }}
                    style={{ display: 'none' }}
                  />
                </>
              )}
            </div>
          </FormField>
          <FormField label="Logo de la organización">
            <div className="photo-upload-container">
              <div className="photo-preview">
                {organizationLogoPreview ? (
                  <img 
                    src={organizationLogoPreview} 
                    alt="Logo de la organización" 
                    className="photo-preview-image"
                    onClick={() => setShowOrganizationLogoModal(true)}
                    style={{ cursor: 'pointer' }}
                  />
                ) : (
                  <div className="photo-placeholder">
                    <p>Sin logo</p>
                  </div>
                )}
              </div>
              {organizationLogoPreview && (
                <ImageModal
                  isOpen={showOrganizationLogoModal}
                  imageUrl={organizationLogoPreview}
                  alt="Logo de la organización"
                  onClose={() => setShowOrganizationLogoModal(false)}
                  onSave={async (editedImageUrl) => {
                    // Convertir la URL del blob a File
                    try {
                      // La URL puede ser blob, data, o una URL de WordPress
                      // Si es una URL de WordPress, necesitamos convertirla a blob primero
                      if (!editedImageUrl) {

                        return;
                      }

                      let blob: Blob;
                      
                      if (editedImageUrl.startsWith('blob:') || editedImageUrl.startsWith('data:')) {
                        // Es un blob o data URL, hacer fetch directamente
                        const response = await fetch(editedImageUrl);
                        if (!response.ok) {
                          throw new Error(`Error al cargar la imagen: ${response.status} ${response.statusText}`);
                        }
                        blob = await response.blob();
                      } else {
                        // Es una URL de WordPress, usar el proxy para evitar CORS
                        let proxyBaseUrl = process.env.REACT_APP_API_URL || window.location.origin;
                        proxyBaseUrl = proxyBaseUrl.replace(/\/api\/?$/, '');
                        const proxyUrl = `${proxyBaseUrl}/api/proxy-image?url=${encodeURIComponent(editedImageUrl)}`;
                        const response = await fetch(proxyUrl);
                        if (!response.ok) {
                          throw new Error(`Error al cargar la imagen: ${response.status} ${response.statusText}`);
                        }
                        blob = await response.blob();
                      }

                      // Extraer el nombre original del archivo de la URL original
                      const getOriginalFileName = (url: string): string => {
                        try {
                          // Si es una URL de WordPress, extraer el nombre del archivo
                          if (url && url.includes('/')) {
                            const urlParts = url.split('/');
                            const fileName = urlParts[urlParts.length - 1];
                            // Remover parámetros de query si existen
                            const fileNameWithoutParams = fileName.split('?')[0];
                            // Si tiene nombre válido, usarlo
                            if (fileNameWithoutParams && fileNameWithoutParams.includes('.')) {
                              return fileNameWithoutParams;
                            }
                          }
                          // Si es un blob o data URL, usar nombre por defecto
                          return 'logo.png';
                        } catch {
                          return 'logo.png';
                        }
                      };

                      const originalFileName = getOriginalFileName(organizationLogoPreview || '');
                      // Extraer nombre y extensión
                      const lastDotIndex = originalFileName.lastIndexOf('.');
                      let fileNameWithoutExt = originalFileName;
                      let fileExtension = '.png';
                      
                      if (lastDotIndex > 0) {
                        fileNameWithoutExt = originalFileName.substring(0, lastDotIndex);
                        fileExtension = originalFileName.substring(lastDotIndex);
                      }
                      
                      // Agregar "-edited" antes de la extensión
                      const editedFileName = `${fileNameWithoutExt}-edited${fileExtension}`;

                      const file = new File([blob], editedFileName, { type: blob.type || 'image/png' });
                      
                      // Actualizar el preview
                      setOrganizationLogoPreview(editedImageUrl);
                      
                      // Subir la imagen editada
                      await handleOrganizationLogoChange(file);
                    } catch (error) {

                      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';

                      // Error silencioso - no mostrar diálogo al usuario
                    }
                  }}
                  allowEdit={!readOnly}
                  isPhoto={true}
                  onPhotoChange={handleOrganizationLogoChangeClick}
                  onPhotoDelete={handleOrganizationLogoDelete}
                />
              )}
              <input
                ref={organizationLogoFileInputRef}
                type="file"
                accept="image/png,image/svg+xml,image/svg,.svg,.ai,.eps,application/postscript,application/illustrator"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleOrganizationLogoChange(file);
                  }
                  // Resetear el input
                  if (e.target) {
                    e.target.value = '';
                  }
                }}
                style={{ display: 'none' }}
              />
              {!readOnly && (
                <button 
                  className="photo-upload-btn"
                  onClick={() => {
                    if (organizationLogoPreview) {
                      setShowOrganizationLogoModal(true);
                    } else {
                      organizationLogoFileInputRef.current?.click();
                    }
                  }}
                >
                  {organizationLogoPreview ? 'Editar Logo' : 'Subir Logo'}
                </button>
              )}
              <p style={{ fontSize: '12px', color: '#64748b', marginTop: '8px', fontStyle: 'italic' }}>
                En caso de que este campo quede vacío, se usará el logo de tu organización para las comunicaciones.
              </p>
            </div>
          </FormField>
        </div>
        <div className="form-row">
          <FormField label="Nombre" required>
            <Input 
              value={localFirstName} 
              onChange={(e) => {
                isTypingRef.current = true;
                const newValue = e.target.value;
                setLocalFirstName(newValue);
                // Actualizar solo el estado local (NO el padre) para que el tag se actualice en tiempo real
                // sin causar re-renders que cierren el input
                // onUpdate actualiza el estado local pero NO el padre para nombre/apellido
                onUpdate(speaker.id, 'firstName', newValue);
              }} 
              onBlur={(e) => {
                isTypingRef.current = false;
                const capitalized = capitalizeFirstLetter(e.target.value);
                setLocalFirstName(capitalized);
                // Actualizar con el valor capitalizado y actualizar el padre
                if (onUpdateWithBlur) {
                  onUpdateWithBlur(speaker.id, 'firstName', capitalized);
                } else {
                  onUpdate(speaker.id, 'firstName', capitalized);
                }
                handleBlur('firstName', capitalized);
              }} 
              placeholder="Nombre"
              disabled={readOnly}
            />
          </FormField>
          <FormField label="Apellido" required>
            <Input 
              value={localLastName} 
              onChange={(e) => {
                isTypingRef.current = true;
                const newValue = e.target.value;
                setLocalLastName(newValue);
                // Actualizar solo el estado local (NO el padre) para que el tag se actualice en tiempo real
                // sin causar re-renders que cierren el input
                // onUpdate actualiza el estado local pero NO el padre para nombre/apellido
                onUpdate(speaker.id, 'lastName', newValue);
              }} 
              onBlur={(e) => {
                isTypingRef.current = false;
                const capitalized = capitalizeFirstLetter(e.target.value);
                setLocalLastName(capitalized);
                // Actualizar con el valor capitalizado y actualizar el padre
                if (onUpdateWithBlur) {
                  onUpdateWithBlur(speaker.id, 'lastName', capitalized);
                } else {
                  onUpdate(speaker.id, 'lastName', capitalized);
                }
                handleBlur('lastName', capitalized);
              }} 
              placeholder="Apellido"
              disabled={readOnly}
            />
          </FormField>
        </div>
        <div className="form-row">
          <FormField label="Nacionalidad" required>
            <SearchableSelect
              value={speaker.nationality || ''}
              onChange={(value) => onUpdate(speaker.id, 'nationality', value)}
              options={countries.map(c => c.name)}
              placeholder="Selecciona un país"
              allowSearch={true}
              disabled={readOnly}
            />
          </FormField>
          <FormField label="Organización" required>
            <Input value={speaker.organization || ''} onChange={(e) => onUpdate(speaker.id, 'organization', e.target.value)} onBlur={(e) => handleBlur('organization', e.target.value)} placeholder="Nombre de la organización" disabled={readOnly} />
          </FormField>
        </div>
        <div className="form-row">
          <FormField label="Cargo o especialidad" required>
            <Input value={speaker.position || ''} onChange={(e) => onUpdate(speaker.id, 'position', e.target.value)} onBlur={(e) => handleBlur('position', e.target.value)} placeholder="Ej: Director de Innovación" disabled={readOnly} />
          </FormField>
          <FormField 
            label="Biografía" 
            required={!speaker.linkedin || speaker.linkedin.trim() === ''}
            error={biographyError}
          >
            <Input
              type="url"
              value={speaker.biography || ''}
              onChange={(e) => {
                const value = e.target.value;
                onUpdate(speaker.id, 'biography', value);
              }}
              onBlur={(e) => {
                const value = e.target.value.trim();
                // Si el usuario escribió algo y no tiene protocolo, agregar https://
                if (value && !value.startsWith('http://') && !value.startsWith('https://')) {
                  const domainRegex = /^(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
                  if (domainRegex.test(value)) {
                    onUpdate(speaker.id, 'biography', `https://${value}`);
                  }
                }
                // Solo validar URL, no mostrar mensaje de obligatorio
                // La validación de obligatorio se hace en el formulario completo
                if (value && !validateUrl(value)) {
                  setBiographyError('Por favor, ingresa una URL válida');
                } else {
                  setBiographyError('');
                }
              }}
              placeholder="https://ejemplo.com/biografia"
              error={!!biographyError}
              disabled={readOnly}
            />
            {(!speaker.linkedin || speaker.linkedin.trim() === '') && (
              <p style={{ 
                fontSize: '12px', 
                color: '#64748b', 
                marginTop: '4px',
                fontStyle: 'italic',
                fontFamily: "'Montserrat', sans-serif"
              }}>
                En caso de ingresar el LinkedIn del orador, este campo puede quedar vacío
              </p>
            )}
          </FormField>
        </div>

        <div className="form-row">
          <FormField label="LinkedIn" error={urlErrors.linkedin}>
            <Input
              type="url"
              value={speaker.linkedin || ''}
              onChange={(e) => {
                const value = e.target.value;
                onUpdate(speaker.id, 'linkedin', value);
              }}
              onBlur={(e) => {
                const value = e.target.value.trim();
                // Si el usuario escribió algo y no tiene protocolo, agregar https://
                if (value && !value.startsWith('http://') && !value.startsWith('https://')) {
                  const domainRegex = /^(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
                  if (domainRegex.test(value)) {
                    onUpdate(speaker.id, 'linkedin', `https://${value}`);
                  }
                }
                // NO validar biografía aquí - la validación de URL es independiente de LinkedIn
                // La validación de obligatorio se hace en el formulario completo
              }}
              placeholder="https://linkedin.com/in/usuario"
              error={!!urlErrors.linkedin}
              disabled={readOnly}
            />
          </FormField>
          <FormField label="Instagram" error={urlErrors.instagram}>
            <Input
              type="url"
              value={speaker.instagram || ''}
              onChange={(e) => {
                const value = e.target.value;
                onUpdate(speaker.id, 'instagram', value);
              }}
              onBlur={(e) => {
                const value = e.target.value.trim();
                // Si el usuario escribió algo y no tiene protocolo, agregar https://
                if (value && !value.startsWith('http://') && !value.startsWith('https://')) {
                  const domainRegex = /^(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
                  if (domainRegex.test(value)) {
                    onUpdate(speaker.id, 'instagram', `https://${value}`);
                  }
                }
              }}
              placeholder="https://instagram.com/usuario"
              error={!!urlErrors.instagram}
              disabled={readOnly}
            />
          </FormField>
        </div>

        <div className="form-row">
          <FormField label="Twitter/X" error={urlErrors.twitter}>
            <Input
              type="url"
              value={speaker.twitter || ''}
              onChange={(e) => {
                const value = e.target.value;
                onUpdate(speaker.id, 'twitter', value);
                // Validar en tiempo real
                if (value.trim() === '') {
                  setUrlErrors({ ...urlErrors, twitter: '' });
                } else if (!validateUrl(value)) {
                  setUrlErrors({ ...urlErrors, twitter: 'Por favor, ingresa una URL válida' });
                } else {
                  setUrlErrors({ ...urlErrors, twitter: '' });
                }
              }}
              onBlur={(e) => {
                const value = e.target.value.trim();
                // Si el usuario escribió algo y no tiene protocolo, agregar https://
                if (value && !value.startsWith('http://') && !value.startsWith('https://')) {
                  const domainRegex = /^(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
                  if (domainRegex.test(value)) {
                    onUpdate(speaker.id, 'twitter', `https://${value}`);
                  }
                }
              }}
              placeholder="https://twitter.com/usuario o https://x.com/usuario"
              error={!!urlErrors.twitter}
              disabled={readOnly}
            />
          </FormField>
        </div>

        <FormField label="Minibiografía" required>
          <Textarea
            value={speaker.description || ''}
            onChange={(e) => onUpdate(speaker.id, 'description', e.target.value)}
            placeholder="Minibiografía del orador..."
            rows={4}
            maxLength={500}
            showCharCount={true}
            disabled={readOnly}
          />
        </FormField>
      </div>
      )}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
        }}
        onConfirm={() => {
          onRemove(speaker.id);
          setShowDeleteModal(false);
        }}
        confirmText="Aceptar"
        cancelText="Cancelar"
      >
        <p>¿Estás seguro de que deseas eliminar este orador?</p>
      </Modal>
    </div>
  );
};

// Ref global para persistir el estado isExpanded de cada tarjeta de evento por su ID
// Se declara fuera del componente para que persista entre re-renders
const eventExpandedStateMap = new Map<string, boolean>();

const ParallelEventCard: React.FC<ParallelEventCardProps> = ({ event, speakers, onUpdate, onRemove, onAddSpeaker, readOnly = false, initialExpanded = false, newLocalSpeakerIds = new Set(), onNewLocalSpeaker, showApprovalBadge = false, onApprovalStatusChange, userId }) => {

  const [filePreviews, setFilePreviews] = useState<Array<{ name: string; data: string }>>([]);
  const [visibleTooltip, setVisibleTooltip] = useState<number | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ left: number; top: number } | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Para eventos existentes (initialExpanded = false), siempre empezar cerrado
  // Para eventos nuevos (initialExpanded = true), empezar expandido
  const [isExpanded, setIsExpanded] = useState<boolean>(initialExpanded);
  const hasBeenInitializedRef = useRef<boolean>(false);
  const hasBeenManuallyToggledRef = useRef<boolean>(false);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  
  // Inicializar el estado: eventos nuevos expandidos, eventos existentes cerrados
  useLayoutEffect(() => {
    if (!hasBeenInitializedRef.current) {
      // Si es un evento nuevo, expandir
      if (initialExpanded) {
        eventExpandedStateMap.set(event.id, true);
        setIsExpanded(true);
      } else {
        // Si es un evento existente, siempre empezar cerrado (ignorar estado persistido previo)
        eventExpandedStateMap.set(event.id, false);
        setIsExpanded(false);
      }
      hasBeenInitializedRef.current = true;
    }
  }, [event.id, initialExpanded]);
  
  // Actualizar el estado persistido cuando isExpanded cambia (solo si fue manualmente toggled)
  useEffect(() => {
    if (hasBeenInitializedRef.current && hasBeenManuallyToggledRef.current) {
      eventExpandedStateMap.set(event.id, isExpanded);
    }
  }, [isExpanded, event.id]);
  const speakerCounterRef = useRef<number>(0);
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);
  const { showFileSizeError } = useFileError();
  const { startUpload, updateProgress, finishUpload, setCurrentFileName } = useUploadProgress();
  const { showTimeout } = useTimeout();
  const [localSpeakerUrlErrors, setLocalSpeakerUrlErrors] = useState<{ linkedin: string; instagram: string; twitter: string }>({
    linkedin: '',
    instagram: '',
    twitter: ''
  });
  const [localSpeakerBiographyError, setLocalSpeakerBiographyError] = useState<string>('');

  // Función de validación de URL (igual al form participar)
  const validateUrl = (url: string): boolean => {
    if (url.trim() === '') return true; // Campo opcional
    
    // Si ya tiene protocolo, validar URL completa
    if (url.startsWith('http://') || url.startsWith('https://')) {
      const urlRegex = /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
      return urlRegex.test(url);
    }
    
    // Si no tiene protocolo, validar solo dominio
    const domainRegex = /^(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
    return domainRegex.test(url);
  };

  // Validar Biografía: obligatorio PERO puede estar vacío si LinkedIn está lleno
  const validateBiography = (biography: string, linkedin: string): boolean => {
    const biographyTrimmed = biography.trim();
    const linkedinTrimmed = linkedin.trim();
    
    // Si LinkedIn está lleno, Biografía puede estar vacío
    if (linkedinTrimmed !== '') {
      return true;
    }
    
    // Si LinkedIn está vacío, Biografía es obligatorio
    return biographyTrimmed !== '';
  };
  // Eliminado: showLocalSpeakerForm ya no se usa. Solo se crean oradores mediante el botón pequeño.
  const [localSpeakerPhotoPreview, setLocalSpeakerPhotoPreview] = useState<string | null>(null);
  const [localSpeakerOrganizationLogoPreview, setLocalSpeakerOrganizationLogoPreview] = useState<string | null>(null);
  const [showLocalSpeakerImageModal, setShowLocalSpeakerImageModal] = useState(false);
  const [showLocalSpeakerOrganizationLogoModal, setShowLocalSpeakerOrganizationLogoModal] = useState(false);
  const localSpeakerPhotoFileInputRef = useRef<HTMLInputElement>(null);
  const localSpeakerOrganizationLogoFileInputRef = useRef<HTMLInputElement>(null);
  // Estado local para mantener el evento actualizado
  const [currentEvent, setCurrentEvent] = useState<ParallelEvent>(event);
  
  // Sincronizar el estado local cuando cambia el prop event desde el padre
  // Usar una referencia para comparar y evitar loops infinitos
  const prevEventRef = useRef<ParallelEvent>(event);
  const isInternalUpdateRef = useRef<boolean>(false);
  const lastLocalSpeakersUpdateRef = useRef<string>('');
  const isAddingSpeakerRef = useRef<boolean>(false); // Prevenir doble ejecución del botón pequeño
  
  // Inicializar currentEvent cuando cambia el event.id (nuevo evento)
  useEffect(() => {
    if (event.id !== currentEvent.id) {
      setCurrentEvent(event);
      prevEventRef.current = event;
      lastLocalSpeakersUpdateRef.current = JSON.stringify((event.localSpeakers || []).map(ls => ls.id).sort());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.id, currentEvent.id]);
  
  useEffect(() => {
    
    // Si fue una actualización interna, no sobrescribir pero actualizar la referencia
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      // Actualizar prevEventRef con el estado local actual (que es más reciente)
      prevEventRef.current = currentEvent;
      return;
    }
    
    // Comparar si el evento del padre realmente cambió
    const prevLocalSpeakersIds = (prevEventRef.current.localSpeakers || []).map(ls => ls.id).sort().join(',');
    const newLocalSpeakersIds = (event.localSpeakers || []).map(ls => ls.id).sort().join(',');
    
    // Comparar también los datos de los oradores para detectar cambios en los campos
    const prevLocalSpeakersData = JSON.stringify((prevEventRef.current.localSpeakers || []).map(ls => ({ id: ls.id, firstName: ls.firstName, lastName: ls.lastName })));
    const newLocalSpeakersData = JSON.stringify((event.localSpeakers || []).map(ls => ({ id: ls.id, firstName: ls.firstName, lastName: ls.lastName })));
    
    // Comparar speakerIds
    const prevSpeakerIds = (prevEventRef.current.speakerIds || []).sort().join(',');
    const newSpeakerIds = (event.speakerIds || []).sort().join(',');
    
    // Comparar el estado local actual con el prop del padre
    const currentLocalSpeakersIds = (currentEvent.localSpeakers || []).map(ls => ls.id).sort().join(',');
    const currentLocalSpeakersData = JSON.stringify((currentEvent.localSpeakers || []).map(ls => ({ id: ls.id, firstName: ls.firstName, lastName: ls.lastName })));
    const currentSpeakerIds = (currentEvent.speakerIds || []).sort().join(',');
    
    // Solo actualizar si:
    // 1. El evento del padre cambió desde la última vez que lo vimos (prevEventRef)
    // 2. Y el estado local NO tiene datos más recientes que el padre
    const parentChanged = prevLocalSpeakersIds !== newLocalSpeakersIds || 
        prevLocalSpeakersData !== newLocalSpeakersData ||
        prevSpeakerIds !== newSpeakerIds ||
        prevEventRef.current.localSpeaker?.id !== event.localSpeaker?.id ||
        prevEventRef.current.name !== event.name ||
        prevEventRef.current.id !== event.id;
    
    const localIsMoreRecent = currentLocalSpeakersIds !== newLocalSpeakersIds || 
        currentLocalSpeakersData !== newLocalSpeakersData ||
        currentSpeakerIds !== newSpeakerIds;
    
    // Si el padre cambió y el estado local no es más reciente, actualizar
    if (parentChanged && !localIsMoreRecent) {
      setCurrentEvent(event);
      prevEventRef.current = event;
      lastLocalSpeakersUpdateRef.current = newLocalSpeakersIds;
    } else if (parentChanged) {
      // El padre cambió pero el estado local es más reciente, actualizar solo la referencia
      prevEventRef.current = event;
    } else {
      // Si no hay cambios, asegurarse de que prevEventRef esté actualizado
      prevEventRef.current = event;
    }
  }, [event, currentEvent]);

  // Detectar si es mobile o tablet
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileOrTablet(window.innerWidth <= 1024);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    const loadFilePreviews = async () => {
      if (!currentEvent.presentationFile || !Array.isArray(currentEvent.presentationFile)) {
        setFilePreviews([]);
        return;
      }

      const previews: Array<{ name: string; data: string }> = [];
      
      for (const doc of currentEvent.presentationFile) {
        try {
          if (doc instanceof File) {
            const reader = new FileReader();
            const preview = await new Promise<string>((resolve, reject) => {
              reader.onloadend = () => resolve(reader.result as string);
              reader.onerror = reject;
              reader.readAsDataURL(doc);
            });
            previews.push({ name: doc.name, data: preview });
          } else if (typeof doc === 'string') {
            // Verificar si tiene formato con nombre: "FILENAME:nombre_archivo:DATA:base64data" (formato antiguo)
            if (doc.startsWith('FILENAME:') && doc.includes(':DATA:')) {
              const parts = doc.split(':DATA:');
              const fileNamePart = parts[0].replace('FILENAME:', '');
              const base64Data = parts[1];
              previews.push({ name: fileNamePart, data: base64Data });
            } 
            // Verificar si tiene formato con URL: "FILENAME:nombre_archivo:URL:url" (formato nuevo con WordPress)
            else if (doc.startsWith('FILENAME:') && doc.includes(':URL:')) {
              const parts = doc.split(':URL:');
              const fileNamePart = parts[0].replace('FILENAME:', '');
              const url = parts[1];
              previews.push({ name: fileNamePart, data: url });
            } 
              // Formato base64 sin nombre guardado (archivos antiguos)
            else if (doc.includes(';base64,')) {
              previews.push({ name: `documento_${previews.length + 1}`, data: doc });
            } 
            // URL directa (WordPress u otra)
            else if (doc.startsWith('http://') || doc.startsWith('https://')) {
              const fileName = doc.split('/').pop()?.split('?')[0] || `documento_${previews.length + 1}`;
              previews.push({ name: fileName, data: doc });
            } 
            // Formato desconocido
            else {
              const fileName = doc.split('/').pop() || `documento_${previews.length + 1}`;
              previews.push({ name: fileName, data: doc });
            }
          }
        } catch (error) {
        }
      }
      
      setFilePreviews(previews);
    };

    loadFilePreviews();
  }, [currentEvent.presentationFile]);

  // Efecto para manejar el tooltip en el DOM
  useEffect(() => {
    if (tooltipRef.current && tooltipRef.current.parentNode) {
      tooltipRef.current.parentNode.removeChild(tooltipRef.current);
      tooltipRef.current = null;
    }

    if (visibleTooltip !== null && tooltipPosition !== null) {
      const ejeSeleccionado = thematicAxes[visibleTooltip];
      const subejes = subejesPorEje[ejeSeleccionado.label]?.subejes || [];
      
      if (subejes.length === 0) return;

      const isDarkMode = document.documentElement.classList.contains('dark-theme');

      const tooltipDiv = document.createElement('div');
      tooltipDiv.className = 'tooltip-content';
      tooltipDiv.style.cssText = `
        position: fixed !important;
        left: ${tooltipPosition.left}px !important;
        top: ${tooltipPosition.top}px !important;
        z-index: 999999 !important;
        background-color: ${isDarkMode ? '#0f172a' : '#ffffff'} !important;
        border: 2px solid #CE2B15 !important;
        border-radius: 8px !important;
        padding: 12px 16px !important;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
        min-width: 280px !important;
        max-width: 400px !important;
        font-family: Montserrat, sans-serif !important;
        pointer-events: none !important;
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
      `;

      const titleDiv = document.createElement('div');
      titleDiv.style.cssText = `font-weight: 700; color: ${isDarkMode ? '#ffffff' : '#1e293b'}; font-size: 13px; margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid ${isDarkMode ? '#334155' : '#e5e7eb'};`;
      titleDiv.textContent = 'Incluye:';

      const ul = document.createElement('ul');
      ul.style.cssText = 'list-style: none; padding: 0; margin: 0;';

      subejes.forEach((subeje) => {
        const li = document.createElement('li');
        li.style.cssText = `font-size: 12px; color: ${isDarkMode ? '#cbd5e1' : '#64748b'}; line-height: 1.6; padding: 4px 0; padding-left: 16px; position: relative;`;
        
        const bullet = document.createElement('span');
        bullet.style.cssText = 'position: absolute; left: 0; color: #CE2B15; font-weight: bold;';
        bullet.textContent = '•';
        
        li.appendChild(bullet);
        li.appendChild(document.createTextNode(subeje));
        ul.appendChild(li);
      });

      tooltipDiv.appendChild(titleDiv);
      tooltipDiv.appendChild(ul);
      
      if (document.body) {
        document.body.appendChild(tooltipDiv);
        tooltipRef.current = tooltipDiv;
      }

      return () => {
        if (tooltipRef.current && tooltipRef.current.parentNode) {
          tooltipRef.current.parentNode.removeChild(tooltipRef.current);
          tooltipRef.current = null;
        }
      };
    }
  }, [visibleTooltip, tooltipPosition]);

  // Cerrar tooltip al hacer clic fuera (solo en mobile/tablet)
  useEffect(() => {
    if (!isMobileOrTablet || visibleTooltip === null) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        const target = event.target as HTMLElement;
        if (!target.closest('.checkbox-item')) {
          setVisibleTooltip(null);
          setTooltipPosition(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileOrTablet, visibleTooltip]);

  const handleFileChange = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    
    const newFiles = Array.from(files);
    
    // Validar tamaño de archivos antes de agregarlos
    const validation = validateMultipleFiles(newFiles, MAX_FILE_SIZE_DOCUMENTS, 'document');
    if (!validation.valid) {
      showFileSizeError(validation.error || 'El archivo excede el tamaño máximo permitido.');
      return;
    }
    
    try {
      // Iniciar progreso de subida
      startUpload(newFiles[0].name, newFiles.length);
      
      // Subir archivos de forma secuencial (uno después del otro)
      const uploadedUrls: string[] = [];
      for (let index = 0; index < newFiles.length; index++) {
        const file = newFiles[index];
        // Actualizar el nombre del archivo actual
        setCurrentFileName(file.name);
        const fileUrl = await uploadToSupabase(file, {
          folder: 'perfil-institucional/parallel-events/presentations',
          onProgress: (progress) => {
            // Calcular progreso total considerando todos los archivos
            // Cada archivo completo representa (100 / totalArchivos)%
            const baseProgress = (index / newFiles.length) * 100;
            const fileProgress = (progress / newFiles.length);
            const totalProgress = baseProgress + fileProgress;
            updateProgress(totalProgress, index);
          }
        });
        // Log para debugging
        
        // Guardar con formato: "FILENAME:nombre_archivo:URL:url"
        uploadedUrls.push(`FILENAME:${file.name}:URL:${fileUrl}`);
      }
      
      // Finalizar progreso
      finishUpload();
      
      const currentDocs = Array.isArray(currentEvent.presentationFile) ? currentEvent.presentationFile : [];
      
      // Log para debugging
      
      // Agregar las URLs al estado
      const updatedFiles = [...currentDocs, ...uploadedUrls];
      setCurrentEvent(prev => ({ ...prev, presentationFile: updatedFiles }));
      onUpdate(currentEvent.id, 'presentationFile', updatedFiles);
    } catch (error: any) {
      // Verificar si es un error de rate limiting (429)
      if (handleRateLimitError(error)) {
        finishUpload();
        return;
      }
      
      // Verificar si es un error de formato o tamaño del backend
      if (handleUploadError(error, showFileSizeError)) {
        finishUpload();
        return;
      }
      
      // Verificar si es un error de timeout
      if (error?.isTimeout) {
        showTimeout(error.operationName || 'la subida de los archivos de presentación');
        finishUpload();
        return;
      }
      
      // Finalizar progreso incluso si hay error
      finishUpload();
      // Error silencioso para otros tipos de error
    }
  };

  const handleRemoveFile = (index: number) => {
    const currentDocs = Array.isArray(currentEvent.presentationFile) ? currentEvent.presentationFile : [];
    const updatedDocs = currentDocs.filter((_, i) => i !== index);
    setCurrentEvent(prev => ({ ...prev, presentationFile: updatedDocs.length > 0 ? updatedDocs : undefined }));
    onUpdate(currentEvent.id, 'presentationFile', updatedDocs.length > 0 ? updatedDocs : undefined);
  };

  // Mostrar el formulario de orador local si hay un localSpeaker guardado
  // Eliminado: El formulario de orador local ya no se usa. Solo se crean oradores mediante el botón pequeño.

  // Efectos para manejar foto del orador local
  useEffect(() => {
    if (!currentEvent.localSpeaker?.photo) {
      setLocalSpeakerPhotoPreview(null);
      return;
    }

    try {
      if (currentEvent.localSpeaker.photo && typeof currentEvent.localSpeaker.photo !== 'string') {
        const reader = new FileReader();
        reader.onloadend = () => {
          setLocalSpeakerPhotoPreview(reader.result as string);
        };
        reader.onerror = () => {
          setLocalSpeakerPhotoPreview(null);
        };
        reader.readAsDataURL(currentEvent.localSpeaker.photo);
      } else if (typeof currentEvent.localSpeaker.photo === 'string' && currentEvent.localSpeaker.photo) {
        // Aceptar: data:image/, http://, https://, o /storage/files/ (proxy URL)
        if (currentEvent.localSpeaker.photo.startsWith('data:image/') || 
            currentEvent.localSpeaker.photo.startsWith('http://') || 
            currentEvent.localSpeaker.photo.startsWith('https://') ||
            currentEvent.localSpeaker.photo.startsWith('/storage/files/')) {
          setLocalSpeakerPhotoPreview(currentEvent.localSpeaker.photo);
        } else {
          setLocalSpeakerPhotoPreview(null);
        }
      } else {
        setLocalSpeakerPhotoPreview(null);
      }
    } catch (error) {
      setLocalSpeakerPhotoPreview(null);
    }
  }, [currentEvent.localSpeaker?.photo]);

  useEffect(() => {
    try {
      if (!currentEvent.localSpeaker) {
        setLocalSpeakerOrganizationLogoPreview(null);
        return;
      }
      if (currentEvent.localSpeaker.organizationLogo && typeof currentEvent.localSpeaker.organizationLogo !== 'string') {
        const reader = new FileReader();
        reader.onloadend = () => setLocalSpeakerOrganizationLogoPreview(reader.result as string);
        reader.onerror = () => setLocalSpeakerOrganizationLogoPreview(null);
        reader.readAsDataURL(currentEvent.localSpeaker.organizationLogo);
      } else if (typeof currentEvent.localSpeaker.organizationLogo === 'string' && currentEvent.localSpeaker.organizationLogo) {
        // Aceptar: data:image/, http://, https://, o /storage/files/ (proxy URL)
        if (currentEvent.localSpeaker.organizationLogo.startsWith('data:image/') || 
            currentEvent.localSpeaker.organizationLogo.startsWith('http://') || 
            currentEvent.localSpeaker.organizationLogo.startsWith('https://') ||
            currentEvent.localSpeaker.organizationLogo.startsWith('/storage/files/')) {
          setLocalSpeakerOrganizationLogoPreview(currentEvent.localSpeaker.organizationLogo);
        } else {
          setLocalSpeakerOrganizationLogoPreview(null);
        }
      } else {
        setLocalSpeakerOrganizationLogoPreview(null);
      }
    } catch (error) {
      setLocalSpeakerOrganizationLogoPreview(null);
    }
  }, [currentEvent.localSpeaker?.organizationLogo]);

  // Sincronizar errores cuando cambia el orador local
  useEffect(() => {
    if (currentEvent.localSpeaker) {
      // Validar Biografía cuando cambia LinkedIn
      const linkedin = currentEvent.localSpeaker.linkedin || '';
      const biography = currentEvent.localSpeaker.biography || '';
      // Solo validar URL, no mostrar mensaje de obligatorio
      // La validación de obligatorio se hace en el formulario completo
      if (biography.trim() === '') {
        setLocalSpeakerBiographyError('');
      } else if (!validateUrl(biography)) {
        setLocalSpeakerBiographyError('Por favor, ingresa una URL válida');
      } else {
        setLocalSpeakerBiographyError('');
      }
    } else {
      // Limpiar errores si no hay orador local
      setLocalSpeakerBiographyError('');
      setLocalSpeakerUrlErrors({ linkedin: '', instagram: '', twitter: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEvent.localSpeaker?.biography]);

  // Funciones para manejar el orador local
  const updateLocalSpeaker = (field: keyof Speaker, value: any) => {
    if (!currentEvent.localSpeaker) return;
    
    // Marcar como actualización interna
    isInternalUpdateRef.current = true;
    
    // Si el orador ya está en la lista localSpeakers, actualizarlo también ahí
    const currentLocalSpeakers = currentEvent.localSpeakers || [];
    const speakerId = currentEvent.localSpeaker.id;
    const speakerIndex = currentLocalSpeakers.findIndex(s => s.id === speakerId);
    
    let updatedLocalSpeakers = currentLocalSpeakers;
    let updatedSpeaker = currentEvent.localSpeaker;
    
    if (speakerIndex !== -1) {
      // El orador ya está en la lista, actualizar ambos (localSpeaker y el de la lista) con el mismo objeto
      updatedLocalSpeakers = currentLocalSpeakers.map(s => 
        s.id === speakerId ? { ...s, [field]: value } : s
      );
      // Crear el mismo objeto actualizado para localSpeaker
      updatedSpeaker = { ...currentEvent.localSpeaker, [field]: value };
    } else {
      // El orador no está en la lista aún, agregarlo a la lista y actualizar localSpeaker
      updatedSpeaker = { ...currentEvent.localSpeaker, [field]: value };
      updatedLocalSpeakers = [...currentLocalSpeakers, updatedSpeaker];
      
      // Notificar al padre sobre el nuevo ID cuando se agrega a la lista por primera vez

      if (onNewLocalSpeaker) {

        flushSync(() => {
          onNewLocalSpeaker(speakerId);
        });

      } else {

      }
    }
    
    // Actualizar el estado local inmediatamente
    setCurrentEvent(prev => ({
      ...prev,
      localSpeaker: updatedSpeaker,
      localSpeakers: updatedLocalSpeakers
    }));
    
    // Actualizar el padre
    onUpdate(currentEvent.id, 'localSpeaker', updatedSpeaker);
    if (speakerIndex !== -1) {
      onUpdate(currentEvent.id, 'localSpeakers', updatedLocalSpeakers);
    }
  };

  const handleLocalSpeakerBlur = (field: keyof Speaker, value: string) => {
    // Capitalizar primera letra solo cuando el usuario termina de escribir (onBlur)
    if (field === 'firstName' || field === 'lastName' || field === 'nationality' || 
        field === 'organization' || field === 'position') {
      const capitalized = capitalizeFirstLetter(value);
      if (capitalized !== value) {
        updateLocalSpeaker(field, capitalized);
      }
    }
  };

  const handleLocalSpeakerPhotoChange = async (file: File) => {
    // Validar tamaño de foto antes de agregarla
    const validation = validateFileSize(file, MAX_FILE_SIZE_IMAGES, 'image');
    if (!validation.valid) {
      showFileSizeError(validation.error || 'La foto excede el tamaño máximo permitido.');
      return;
    }
    
    try {
      // Mostrar preview inmediato
      const reader = new FileReader();
      reader.onloadend = () => {
        const previewUrl = reader.result as string;
        setLocalSpeakerPhotoPreview(previewUrl);
        // Si el modal está abierto, mantenerlo abierto con la nueva imagen
        if (showLocalSpeakerImageModal) {
          // El modal se actualizará automáticamente porque imageUrl cambia
        }
      };
      reader.readAsDataURL(file);
      
      // Iniciar progreso de subida
      startUpload(file.name, 1);
      
      // Subir inmediatamente a Supabase
      const photoUrl = await uploadToSupabase(file, {
        folder: 'perfil-institucional/parallel-events/local-speakers/photos',
        onProgress: (progress) => {
          updateProgress(progress, 0);
        }
      });
      
      // Finalizar progreso
      finishUpload();
      
      // Log para debugging
      
      // Actualizar con la URL de Supabase
      updateLocalSpeaker('photo', photoUrl);
      
      // Forzar actualización del preview inmediatamente
      if (photoUrl) {
        setLocalSpeakerPhotoPreview(photoUrl);
      }
    } catch (error: any) {
      // Verificar si es un error de rate limiting (429)
      if (handleRateLimitError(error)) {
        finishUpload();
        return;
      }
      
      // Verificar si es un error de formato o tamaño del backend
      if (handleUploadError(error, showFileSizeError)) {
        finishUpload();
        return;
      }
      
      // Verificar si es un error de timeout
      if (error?.isTimeout) {
        showTimeout(error.operationName || 'la subida de la foto');
        finishUpload();
        return;
      }
      
      // Finalizar progreso incluso si hay error
      finishUpload();
      // Error silencioso para otros tipos de error
    }
  };

  const handleLocalSpeakerPhotoChangeClick = () => {
    localSpeakerPhotoFileInputRef.current?.click();
  };

  const handleLocalSpeakerPhotoDelete = async () => {
    // Limpiar el preview
    setLocalSpeakerPhotoPreview(null);
    // Actualizar el orador sin foto
    updateLocalSpeaker('photo', undefined);
  };

  const handleLocalSpeakerOrganizationLogoChange = async (file: File) => {
    // Validar tamaño de logo antes de agregarlo
    const validation = validateFileSize(file, MAX_FILE_SIZE_IMAGES, 'image');
    if (!validation.valid) {
      showFileSizeError(validation.error || 'El logo excede el tamaño máximo permitido.');
      return;
    }
    
    try {
      // Mostrar preview inmediato
      const reader = new FileReader();
      reader.onloadend = () => {
        const previewUrl = reader.result as string;
        setLocalSpeakerOrganizationLogoPreview(previewUrl);
        // Si el modal está abierto, mantenerlo abierto con la nueva imagen
        if (showLocalSpeakerOrganizationLogoModal) {
          // El modal se actualizará automáticamente porque imageUrl cambia
        }
      };
      reader.readAsDataURL(file);
      
      // Iniciar progreso de subida
      startUpload(file.name, 1);
      
      // Subir inmediatamente a Supabase
      const logoUrl = await uploadToSupabase(file, {
        folder: 'perfil-institucional/parallel-events/local-speakers/organization-logos',
        onProgress: (progress) => {
          updateProgress(progress, 0);
        }
      });
      
      // Finalizar progreso
      finishUpload();
      
      // Log para debugging
      
      // Actualizar con la URL de Supabase
      updateLocalSpeaker('organizationLogo', logoUrl);
      
      // Forzar actualización del preview inmediatamente
      if (logoUrl) {
        setLocalSpeakerOrganizationLogoPreview(logoUrl);
      }
    } catch (error: any) {
      // Verificar si es un error de rate limiting (429)
      if (handleRateLimitError(error)) {
        finishUpload();
        return;
      }
      
      // Verificar si es un error de formato o tamaño del backend
      if (handleUploadError(error, showFileSizeError)) {
        finishUpload();
        return;
      }
      
      // Verificar si es un error de timeout
      if (error?.isTimeout) {
        showTimeout(error.operationName || 'la subida del logo de la organización');
        finishUpload();
        return;
      }
      
      // Finalizar progreso incluso si hay error
      finishUpload();
      // Error silencioso para otros tipos de error
    }
  };

  const handleLocalSpeakerOrganizationLogoChangeClick = () => {
    localSpeakerOrganizationLogoFileInputRef.current?.click();
  };

  const handleLocalSpeakerOrganizationLogoDelete = async () => {
    // Limpiar el preview
    setLocalSpeakerOrganizationLogoPreview(null);
    // Actualizar el orador sin logo
    updateLocalSpeaker('organizationLogo', undefined);
  };

  // Función para agregar orador local (no se usa actualmente, el botón pequeño agrega directamente)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleAddLocalSpeaker = (e?: React.MouseEvent<HTMLButtonElement>) => {
    // Esta función no se usa actualmente - el botón pequeño agrega directamente
    // Se mantiene comentada para referencia futura
      return;
  };
  
  const handleRemoveLocalSpeaker = (speakerId: string) => {
    const currentLocalSpeakers = currentEvent.localSpeakers || [];
    const updatedLocalSpeakers = currentLocalSpeakers.filter(s => s.id !== speakerId);
    // Marcar como actualización interna
    isInternalUpdateRef.current = true;
    // Actualizar el estado local inmediatamente
    setCurrentEvent(prev => ({ ...prev, localSpeakers: updatedLocalSpeakers.length > 0 ? updatedLocalSpeakers : undefined }));
    onUpdate(currentEvent.id, 'localSpeakers', updatedLocalSpeakers.length > 0 ? updatedLocalSpeakers : undefined);
  };

  // Handler para capitalizar el título cuando se pierde el foco
  const handleNameBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const capitalized = capitalizeFirstLetter(value);
    if (capitalized !== value) {
      setCurrentEvent(prev => ({ ...prev, name: capitalized }));
      onUpdate(currentEvent.id, 'name', capitalized);
    }
  };

  return (
    <div className="parallel-event-card" style={{ position: 'relative', overflow: 'visible' }}>
      {/* Badges de estado en el borde superior derecho (solo en readonly del popup) */}
      {readOnly && showApprovalBadge && (() => {
        const statusInfo = getEventStatusHelper(currentEvent);
        // Solo mostrar selector de aprobación si está completo o tiene solicitud de aprobación
        const shouldShowApprovalSelector = statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval;
        
        return (
          <div className="admin-participation-card-status-badges" style={{ marginRight: '10px' }}>
            {/* Badge de estado de completitud - siempre se muestra si corresponde */}
            <span className={`participation-status-badge status-${statusInfo.completionStatus}`}>
              {statusInfo.completionStatus === 'complete' && <><CheckCircleIcon width={12} height={12} /> Completo</>}
              {statusInfo.completionStatus === 'incomplete' && <><XCircleIcon width={12} height={12} /> Incompleto</>}
            </span>
            {/* Badge de solicitado - siempre se muestra si corresponde */}
            {statusInfo.hasRequestApproval && (
              <span className="participation-status-badge status-requested">
                <AlertTriangleIcon width={12} height={12} /> Solicitado
              </span>
            )}
            {/* Selector de estado de aprobación - solo se muestra si está completo o tiene solicitud */}
            {shouldShowApprovalSelector && onApprovalStatusChange && (
              <div style={{ minWidth: '120px', maxWidth: '140px', flexShrink: 0 }}>
                <CustomSelect
                  className="admin-filter-select admin-filter-select-small"
                  value={currentEvent.approval_status || 'pending'}
                  onChange={(newStatus) => {
                    if (onApprovalStatusChange) {
                      onApprovalStatusChange(currentEvent.id, newStatus as 'pending' | 'approved' | 'rejected');
                    }
                  }}
                  options={[
                    { value: 'pending', label: 'Pendiente', color: '#3b82f6' },
                    { value: 'approved', label: 'Aprobado', color: '#10b981' },
                    { value: 'rejected', label: 'Rechazado', color: '#ef4444' }
                  ]}
                />
              </div>
            )}
          </div>
        );
      })()}
      <div className="event-header">
        <h3>{currentEvent.name || 'Nuevo Evento Paralelo'}</h3>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            type="button"
            className="minimize-button"
            onClick={() => {
              hasBeenManuallyToggledRef.current = true;
              const newExpanded = !isExpanded;
              setIsExpanded(newExpanded);
              eventExpandedStateMap.set(event.id, newExpanded);
            }}
            title={isExpanded ? 'Minimizar' : 'Expandir'}
            style={{
              background: 'transparent',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              padding: '6px 12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#64748b',
              fontSize: '14px',
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 500
            }}
          >
            {isExpanded ? '−' : '+'}
          </button>
          {!readOnly && (
          <button 
            type="button" 
            className="remove-button"
              onClick={() => setShowDeleteModal(true)}
          >
            Eliminar
          </button>
          )}
        </div>
      </div>

      {isExpanded && (
      <div className="parallel-event-card-content">
      <FormField label="Título de la actividad" required>
          <Input
            value={currentEvent.name}
            onChange={(e) => {
              setCurrentEvent(prev => ({ ...prev, name: e.target.value }));
              onUpdate(currentEvent.id, 'name', e.target.value);
            }}
            onBlur={handleNameBlur}
          placeholder="Título de la actividad"
          maxLength={50}
          disabled={readOnly}
          />
        </FormField>

      <FormField label="Descripción" required>
        <Textarea
          value={currentEvent.description}
          onChange={(e) => {
            setCurrentEvent(prev => ({ ...prev, description: e.target.value }));
            onUpdate(currentEvent.id, 'description', e.target.value);
          }}
          placeholder="Descripción de la actividad..."
          rows={4}
          maxLength={700}
          showCharCount={true}
          disabled={readOnly}
        />
      </FormField>

      <div className={`form-row ${(currentEvent.localSpeakers && currentEvent.localSpeakers.length > 0) ? 'before-speakers-list' : ''}`}>
        <FormField label="Oradores" required>
          {(() => {
            const localSpeakers = currentEvent.localSpeakers || [];
            
            // Combinar oradores principales y locales
            const allSpeakers = [...speakers];
            localSpeakers.forEach((localSpeaker) => {
              if (!allSpeakers.find(s => s.id === localSpeaker.id)) {
                allSpeakers.push(localSpeaker);
              }
            });
            
            // Combinar IDs seleccionados
            const selectedSpeakerIds = currentEvent.speakerIds || (currentEvent.speakerId ? [currentEvent.speakerId] : []);
            const localSpeakerIds = localSpeakers.map(ls => ls.id).filter(id => id);
            const allSelectedIds = Array.from(new Set([...selectedSpeakerIds, ...localSpeakerIds]));
            
            // Key completamente estable: solo usa el ID del evento (nunca cambia)
            // Esto evita que el componente se desmonte cuando se editan los nombres
            const componentKey = `speakers-${currentEvent.id}`;

            return (
          <MultiSelectSpeakers
                key={componentKey}
                selectedValues={allSelectedIds}
                speakers={allSpeakers}
                disabled={readOnly}
            onChange={(speakerIds) => {
                  // Separar oradores principales de locales
                  const mainSpeakerIds = speakerIds.filter(id => 
                    speakers.some(s => s.id === id)
                  );
                  const remainingLocalIds = speakerIds.filter(id => 
                    localSpeakers.some(ls => ls.id === id)
                  );

                  // Actualizar lista de oradores locales (eliminar los que fueron removidos)
                  const updatedLocalSpeakers = localSpeakers.filter(ls => 
                    remainingLocalIds.includes(ls.id)
                  );

                  // Marcar como actualización interna
                  isInternalUpdateRef.current = true;

                  // Actualizar el estado local inmediatamente
                  setCurrentEvent(prev => ({
                    ...prev,
                    speakerIds: mainSpeakerIds.length > 0 ? mainSpeakerIds : undefined,
                    speakerId: mainSpeakerIds.length > 0 ? mainSpeakerIds[0] : undefined,
                    localSpeakers: updatedLocalSpeakers.length > 0 ? updatedLocalSpeakers : undefined
                  }));

                  // Actualizar oradores principales seleccionados en el padre
                  onUpdate(currentEvent.id, 'speakerIds', mainSpeakerIds.length > 0 ? mainSpeakerIds : undefined);
              // Mantener compatibilidad con speakerId antiguo
                  if (mainSpeakerIds.length > 0) {
                    onUpdate(currentEvent.id, 'speakerId', mainSpeakerIds[0]);
                  } else {
                    onUpdate(currentEvent.id, 'speakerId', undefined);
              }

                  // Actualizar lista de oradores locales en el padre si cambió
                  if (updatedLocalSpeakers.length !== localSpeakers.length) {
                    onUpdate(currentEvent.id, 'localSpeakers', updatedLocalSpeakers.length > 0 ? updatedLocalSpeakers : undefined);
                  }
                }}
          />
            );
          })()}
          {/* Botón pequeño - siempre visible, debajo del campo Oradores cuando no hay tarjetas */}
          {!readOnly && (!currentEvent.localSpeakers || currentEvent.localSpeakers.length === 0) && (
            <p 
              style={{ 
                marginTop: '8px', 
                color: '#CE2B15', 
                cursor: 'pointer', 
                fontSize: '14px',
                fontWeight: 600,
                fontFamily: 'Montserrat, sans-serif'
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                
                // Prevenir ejecución múltiple
                if (isAddingSpeakerRef.current) {
                  return;
                }
                
                // Marcar que estamos agregando
                isAddingSpeakerRef.current = true;
                
                // Generar ID único
                speakerCounterRef.current += 1;
                const timestamp = Date.now();
                const random1 = Math.random().toString(36).substr(2, 9);
                const random2 = Math.random().toString(36).substr(2, 9);
                const random3 = Math.random().toString(36).substr(2, 9);
                const counter = speakerCounterRef.current;
                const uniqueId = `local_${currentEvent.id}_${timestamp}_${counter}_${random1}_${random2}_${random3}`;
                
                // Crear nuevo orador
                const newLocalSpeaker: Speaker = {
                  id: uniqueId,
                  firstName: '',
                  lastName: '',
                  nationality: '',
                  position: '',
                  organization: '',
                  description: ''
                };
                
                // Notificar al padre sobre el nuevo ID ANTES de actualizar el estado
                // Esto asegura que el ID esté en newLocalSpeakerIds cuando se renderice

                if (onNewLocalSpeaker) {

                  flushSync(() => {
                    onNewLocalSpeaker(uniqueId);
                  });

                } else {

                }
                
                // Agregar a la lista
                const currentLocalSpeakers = currentEvent.localSpeakers || [];
                const updatedLocalSpeakers = [...currentLocalSpeakers, newLocalSpeaker];
                
                // Marcar como actualización interna
                isInternalUpdateRef.current = true;
                const updatedLocalSpeakersIds = updatedLocalSpeakers.map(ls => ls.id).sort().join(',');
                lastLocalSpeakersUpdateRef.current = updatedLocalSpeakersIds;
                
                // Actualizar estado local
                flushSync(() => {
                  setCurrentEvent(prev => ({
                    ...prev,
                    localSpeakers: updatedLocalSpeakers
                  }));
                });
                
                // Actualizar padre
                flushSync(() => {
                  onUpdate(currentEvent.id, 'localSpeakers', updatedLocalSpeakers);
                });
                
                // Actualizar prevEventRef
                prevEventRef.current = {
                  ...currentEvent,
                  localSpeakers: updatedLocalSpeakers
                };
                
                // Resetear flag
                setTimeout(() => {
                  isAddingSpeakerRef.current = false;
                }, 100);
              }}
            >
              + Agregar orador
            </p>
          )}
        </FormField>

        <FormField label="Duración (en minutos)" required>
          <Input
            type="number"
            value={currentEvent.duration}
            onChange={(e) => {
              setCurrentEvent(prev => ({ ...prev, duration: e.target.value }));
              onUpdate(currentEvent.id, 'duration', e.target.value);
            }}
            placeholder="Ej: 30, 60"
            min="1"
            disabled={readOnly}
          />
        </FormField>
      </div>

      {/* Lista de oradores locales agregados */}
      {currentEvent.localSpeakers && currentEvent.localSpeakers.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {currentEvent.localSpeakers.map((localSpeaker, index) => {
                const updateLocalSpeakerInList = (speakerId: string, field: keyof Speaker, value: any) => {
                  const currentLocalSpeakers = currentEvent.localSpeakers || [];
                  const updatedLocalSpeakers = currentLocalSpeakers.map(s => 
                    s.id === speakerId ? { ...s, [field]: value } : s
                  );
                  // Marcar como actualización interna
                  isInternalUpdateRef.current = true;
                  // Actualizar el estado local inmediatamente (para que el tag se actualice en tiempo real)
                  setCurrentEvent(prev => ({ ...prev, localSpeakers: updatedLocalSpeakers }));
                  
                  // Para nombre/apellido, NO actualizar el padre aquí (se actualizará en onBlur)
                  // Para otros campos, actualizar el padre inmediatamente
                  if (field !== 'firstName' && field !== 'lastName') {
                    onUpdate(currentEvent.id, 'localSpeakers', updatedLocalSpeakers);
                  }
                };
                
                // Función separada para actualizar el padre (solo se llama desde onBlur)
                const updateLocalSpeakerInListAndParent = (speakerId: string, field: keyof Speaker, value: any) => {
                  const currentLocalSpeakers = currentEvent.localSpeakers || [];
                  const updatedLocalSpeakers = currentLocalSpeakers.map(s => 
                    s.id === speakerId ? { ...s, [field]: value } : s
                  );
                  // Marcar como actualización interna
                  isInternalUpdateRef.current = true;
                  // Actualizar el estado local
                  setCurrentEvent(prev => ({ ...prev, localSpeakers: updatedLocalSpeakers }));
                  // Actualizar el padre
                  onUpdate(currentEvent.id, 'localSpeakers', updatedLocalSpeakers);
                };

                // Wrapper para onUpdate que distingue entre onChange y onBlur
                const handleUpdate = (speakerId: string, field: keyof Speaker, value: any, fromBlur: boolean = false) => {
                  if (fromBlur && (field === 'firstName' || field === 'lastName')) {
                    // Si viene de onBlur, usar updateLocalSpeakerInListAndParent
                    updateLocalSpeakerInListAndParent(speakerId, field, value);
                  } else {
                    // Si viene de onChange o es otro campo, usar updateLocalSpeakerInList
                    updateLocalSpeakerInList(speakerId, field, value);
                  }
                };

                const isInNewSet = newLocalSpeakerIds.has(localSpeaker.id);

                return (
                  <LocalSpeakerCard
                    key={localSpeaker.id || `local-speaker-${index}`}
                    speaker={localSpeaker}
                    onUpdate={(id, field, value) => handleUpdate(id, field, value, false)}
                    onUpdateWithBlur={(id, field, value) => handleUpdate(id, field, value, true)}
                    onRemove={handleRemoveLocalSpeaker}
                    readOnly={readOnly}
                    initialExpanded={isInNewSet}
                    newLocalSpeakerIds={newLocalSpeakerIds}
                  />
                );
              })}
            </div>
          )}

      {/* Botón pequeño - aparece debajo de la última tarjeta cuando hay tarjetas */}
      {!readOnly && currentEvent.localSpeakers && currentEvent.localSpeakers.length > 0 && (
        <p 
          style={{ 
            marginTop: '20px',
            marginBottom: '20px',
            color: '#CE2B15', 
            cursor: 'pointer', 
            fontSize: '14px',
            fontWeight: 600,
            fontFamily: 'Montserrat, sans-serif'
          }}
          onClick={(e) => {

            e.preventDefault();
            e.stopPropagation();
            
            // Prevenir ejecución múltiple
            if (isAddingSpeakerRef.current) {

              return;
            }
            
            // Marcar que estamos agregando
            isAddingSpeakerRef.current = true;
            
            // Generar ID único
            speakerCounterRef.current += 1;
            const timestamp = Date.now();
            const random1 = Math.random().toString(36).substr(2, 9);
            const random2 = Math.random().toString(36).substr(2, 9);
            const random3 = Math.random().toString(36).substr(2, 9);
            const counter = speakerCounterRef.current;
            const uniqueId = `local_${currentEvent.id}_${timestamp}_${counter}_${random1}_${random2}_${random3}`;
            
            // Crear nuevo orador
            const newLocalSpeaker: Speaker = {
              id: uniqueId,
              firstName: '',
              lastName: '',
              nationality: '',
              position: '',
              organization: '',
              description: '',
              biography: '',
              linkedin: '',
              instagram: '',
              twitter: ''
            };
            
            // Agregar a la lista AL FINAL (asegurar orden correcto)
            const currentLocalSpeakers = currentEvent.localSpeakers || [];
            const updatedLocalSpeakers = [...currentLocalSpeakers, newLocalSpeaker];
            
            // Notificar al padre sobre el nuevo ID ANTES de actualizar el estado
            // Esto asegura que el ID esté en newLocalSpeakerIds cuando se renderice

            if (onNewLocalSpeaker) {

              flushSync(() => {

                onNewLocalSpeaker(uniqueId);

              });

            } else {

            }
            
            // Marcar como actualización interna
            isInternalUpdateRef.current = true;
            const updatedLocalSpeakersIds = updatedLocalSpeakers.map(ls => ls.id).sort().join(',');
            lastLocalSpeakersUpdateRef.current = updatedLocalSpeakersIds;
            
            // Actualizar estado local
            flushSync(() => {
              setCurrentEvent(prev => ({
                ...prev,
                localSpeakers: updatedLocalSpeakers
              }));
            });
            
            // Actualizar padre
            flushSync(() => {
              onUpdate(currentEvent.id, 'localSpeakers', updatedLocalSpeakers);
            });
            
            // Actualizar prevEventRef
            prevEventRef.current = {
              ...currentEvent,
              localSpeakers: updatedLocalSpeakers
            };
            
            // Resetear flag
            setTimeout(() => {
              isAddingSpeakerRef.current = false;
            }, 100);
          }}
        >
          + Agregar orador
        </p>
      )}

      <FormField 
        label="Ejes de la actividad" 
        required
        helpText={
          <>
            Selecciona uno o varios ejes temáticos (Conoce más de cada eje en{' '}
            <a 
              href="https://smartcityexposantiagodelestero.com/tematicas" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ 
                color: '#CE2B15', 
                textDecoration: 'none',
                fontWeight: 600
              }}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              Temáticas
            </a>
            )
          </>
        }
      >
        <div className="checkbox-group thematic-axes-two-columns">
          {/* Selector "Seleccionar todos" */}
          <label 
            className="checkbox-item select-all-checkbox"
            style={{
              fontWeight: 600,
              marginBottom: '8px',
              width: '100%'
            }}
          >
            <input
              type="checkbox"
              checked={
                (currentEvent.thematicAxes || []).length > 0 &&
                (currentEvent.thematicAxes || []).length === thematicAxes.length
              }
              onChange={(e) => {
                if (readOnly) return;
                const currentAxes = currentEvent.thematicAxes || [];
                let updatedAxes: ThematicAxis[];
                let updatedAttendeeProfiles = currentEvent.attendeeProfiles || [];
                
                if (e.target.checked) {
                  // Seleccionar todos
                  updatedAxes = thematicAxes.map(axis => axis.value) as ThematicAxis[];
                } else {
                  // Deseleccionar todos
                  updatedAxes = [];
                  // Eliminar todos los perfiles de asistentes cuando se deseleccionan todos los ejes
                  updatedAttendeeProfiles = [];
                }
                
                setCurrentEvent(prev => ({ 
                  ...prev, 
                  thematicAxes: updatedAxes,
                  attendeeProfiles: updatedAttendeeProfiles
                }));
                onUpdate(currentEvent.id, 'thematicAxes', updatedAxes);
                if (updatedAttendeeProfiles.length !== (currentEvent.attendeeProfiles || []).length) {
                  onUpdate(currentEvent.id, 'attendeeProfiles', updatedAttendeeProfiles);
                }
              }}
              disabled={readOnly}
            />
            <span className="checkbox-label">Seleccionar todos</span>
          </label>
          {thematicAxes.map((option, index) => {
            const subejes = subejesPorEje[option.label]?.subejes || [];
            const hasSubejes = subejes.length > 0;
            const isChecked = currentEvent.thematicAxes?.includes(option.value) || false;
            
            return (
              <label 
                key={option.value} 
                className={`checkbox-item ${hasSubejes ? 'has-tooltip' : ''}`}
                onMouseEnter={(e) => {
                  if (hasSubejes && !isMobileOrTablet) {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const tooltipWidth = 300;
                    const spacing = 10;
                    
                    let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
                    let top = rect.bottom + spacing;
                    
                    if (left + tooltipWidth > window.innerWidth) {
                      left = window.innerWidth - tooltipWidth - 10;
                    }
                    if (left < 10) {
                      left = 10;
                    }
                    
                    const tooltipHeight = 200;
                    if (top + tooltipHeight > window.innerHeight) {
                      top = rect.top - tooltipHeight - spacing;
                    }
                    if (top < 10) {
                      top = 10;
                    }
                    
                    setVisibleTooltip(index);
                    setTooltipPosition({ left, top });
                  }
                }}
                onMouseLeave={() => {
                  if (!isMobileOrTablet) {
                    setVisibleTooltip(null);
                    setTooltipPosition(null);
                  }
                }}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={(e) => {
                    if (readOnly) return;
                    const currentAxes = currentEvent.thematicAxes || [];
                    const updatedAxes = e.target.checked 
                      ? [...currentAxes, option.value]
                      : currentAxes.filter(axis => axis !== option.value);
                    
                    // Si se deselecciona una temática, eliminar los perfiles de asistentes de esa temática
                    let updatedAttendeeProfiles = currentEvent.attendeeProfiles || [];
                    if (!e.target.checked) {
                      // Obtener el label de la temática que se está deseleccionando
                      const deselectedAxisLabel = option.label;
                      
                      // Filtrar los perfiles de asistentes para eliminar los que pertenecen a la temática deseleccionada
                      updatedAttendeeProfiles = (currentEvent.attendeeProfiles || []).filter(ap => {
                        const profileThematicAxis = typeof ap === 'string' ? '' : ap.thematicAxis;
                        return profileThematicAxis !== deselectedAxisLabel;
                      });
                    }
                    
                    setCurrentEvent(prev => ({ 
                      ...prev, 
                      thematicAxes: updatedAxes,
                      attendeeProfiles: updatedAttendeeProfiles
                    }));
                    onUpdate(currentEvent.id, 'thematicAxes', updatedAxes);
                    if (!e.target.checked && updatedAttendeeProfiles.length !== (currentEvent.attendeeProfiles || []).length) {
                      // Solo actualizar attendeeProfiles si hubo cambios
                      onUpdate(currentEvent.id, 'attendeeProfiles', updatedAttendeeProfiles);
                    }
                  }}
                  disabled={readOnly}
                />
                <span className="checkbox-label">{option.label}</span>
                {hasSubejes && isMobileOrTablet && (
                  <span
                    className="modalidad-info-icon"
                    style={{
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: '#f3f4f6',
                      color: '#6b7280',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      border: '1px solid #d1d5db',
                      flexShrink: 0,
                      transition: 'all 0.2s ease',
                      marginLeft: '8px',
                      display: 'flex'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      
                      if (visibleTooltip === index) {
                        setVisibleTooltip(null);
                        setTooltipPosition(null);
                        return;
                      }
                      
                      const rect = e.currentTarget.getBoundingClientRect();
                      const tooltipWidth = 300;
                      const spacing = 10;
                      
                      let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
                      let top = rect.bottom + spacing;
                      
                      if (left + tooltipWidth > window.innerWidth) {
                        left = window.innerWidth - tooltipWidth - 10;
                      }
                      if (left < 10) {
                        left = 10;
                      }
                      
                      const tooltipHeight = 200;
                      if (top + tooltipHeight > window.innerHeight) {
                        top = rect.top - tooltipHeight - spacing;
                      }
                      if (top < 10) {
                        top = 10;
                      }
                      
                      setVisibleTooltip(index);
                      setTooltipPosition({ left, top });
                    }}
                  >
                    ?
                  </span>
                )}
              </label>
            );
          })}
        </div>
      </FormField>

      <FormField 
        label="Perfil de asistentes"
        required
      >
        {(() => {
          // Obtener todos los perfiles agrupados por temática de los ejes temáticos seleccionados
          const selectedAxesLabels = (currentEvent.thematicAxes || []).map(axisValue => {
            const axis = thematicAxes.find(a => a.value === axisValue);
            return axis ? axis.label : '';
          }).filter(label => label !== '');

          // Agrupar perfiles por temática
          const groupedProfiles: Record<string, string[]> = {};
          selectedAxesLabels.forEach(axisLabel => {
            const profiles = attendeeProfilesByThematicAxis[axisLabel] || [];
            if (profiles.length > 0) {
              groupedProfiles[axisLabel] = profiles;
            }
          });

          // Calcular total de perfiles únicos para el placeholder
          const allProfiles: string[] = [];
          Object.values(groupedProfiles).forEach(profiles => {
            profiles.forEach(profile => {
              if (!allProfiles.includes(profile)) {
                allProfiles.push(profile);
              }
            });
          });

          // Convertir attendeeProfiles a formato de strings para el componente (solo para mostrar)
          const selectedProfileStrings = (currentEvent.attendeeProfiles || []).map(ap => 
            typeof ap === 'string' ? ap : ap.profile
          );

          // Función para manejar cambios que incluye la temática
          const handleAttendeeProfilesChange = (profileStrings: string[]) => {
            // Crear array de AttendeeProfile con temática
            const newAttendeeProfiles: AttendeeProfile[] = [];
            
            profileStrings.forEach(profileString => {
              // Buscar en qué temática está este perfil
              let foundThematicAxis = '';
              Object.keys(groupedProfiles).forEach(axisLabel => {
                if (groupedProfiles[axisLabel].includes(profileString)) {
                  foundThematicAxis = axisLabel;
                }
              });
              
              if (foundThematicAxis) {
                newAttendeeProfiles.push({
                  profile: profileString,
                  thematicAxis: foundThematicAxis
                });
              }
            });
            
            setCurrentEvent(prev => ({ ...prev, attendeeProfiles: newAttendeeProfiles }));
            onUpdate(currentEvent.id, 'attendeeProfiles', newAttendeeProfiles);
          };

          return (
            <MultiSelectTags
              groupedOptions={Object.keys(groupedProfiles).length > 0 ? groupedProfiles : undefined}
              selectedValues={selectedProfileStrings}
              onChange={handleAttendeeProfilesChange}
              placeholder={allProfiles.length > 0 ? "Seleccionar perfiles de asistentes..." : "Primero selecciona ejes temáticos"}
              disabled={readOnly || allProfiles.length === 0}
            />
          );
        })()}
      </FormField>

      <FormField 
        label="Documentos de apoyo"
      >
        <div className="file-upload-container">
          {filePreviews.length > 0 ? (
            <div className="files-list">
              {filePreviews.map((preview, index) => (
                <div key={index} className="file-preview">
                  <p className="file-preview-text">{preview.name}</p>
              {!readOnly && (
              <button
                type="button"
                className="remove-file-button"
                    onClick={() => handleRemoveFile(index)}
              >
                    Eliminar
              </button>
              )}
                </div>
              ))}
            </div>
          ) : (
            <div className="file-placeholder">
              <span>📄</span>
              <p>Sin archivos</p>
            </div>
          )}
          {!readOnly && (
          <label className="file-upload-btn">
            <input
              type="file"
              multiple
              onChange={(e) => handleFileChange(e.target.files)}
              style={{ display: 'none' }}
            />
            Agregar Archivos
          </label>
          )}
        </div>
      </FormField>

      {/* Checkbox Solicitar Aprobación */}
        <div style={{ marginTop: '20px' }}>
          <div className="checkbox-group">
            <label className="checkbox-item">
              <input
                type="checkbox"
                checked={currentEvent.requestApproval || false}
                onChange={(e) => {
                if (!readOnly) {
                  setCurrentEvent(prev => ({ ...prev, requestApproval: e.target.checked }));
                  onUpdate(currentEvent.id, 'requestApproval', e.target.checked);
                }
                }}
              disabled={readOnly}
              />
              <span className="checkbox-label">Solicitar aprobación del evento paralelo</span>
            </label>
          </div>
        </div>
        
        {/* Badges de estado al final (solo en readonly del popup) */}
        {readOnly && showApprovalBadge && (() => {
          const statusInfo = getEventStatusHelper(currentEvent);
          // Solo mostrar selector de aprobación si está completo o tiene solicitud de aprobación
          const shouldShowApprovalSelector = statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval;
          
          return (
            <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'nowrap' }}>
                {/* Badge de estado de completitud - siempre se muestra si corresponde */}
                <span className={`participation-status-badge status-${statusInfo.completionStatus}`}>
                  {statusInfo.completionStatus === 'complete' && <><CheckCircleIcon width={12} height={12} /> Completo</>}
                  {statusInfo.completionStatus === 'incomplete' && <><XCircleIcon width={12} height={12} /> Incompleto</>}
                </span>
                {/* Badge de solicitado - siempre se muestra si corresponde */}
                {statusInfo.hasRequestApproval && (
                  <span className="participation-status-badge status-requested">
                    <AlertTriangleIcon width={12} height={12} /> Solicitado
                  </span>
                )}
                {/* Selector de estado de aprobación - solo se muestra si está completo o tiene solicitud */}
                {shouldShowApprovalSelector && onApprovalStatusChange && (
                  <div style={{ minWidth: '120px', maxWidth: '140px', flexShrink: 0 }}>
                    <CustomSelect
                      className="admin-filter-select admin-filter-select-small"
                      value={currentEvent.approval_status || 'pending'}
                      onChange={(newStatus) => {
                        if (onApprovalStatusChange) {
                          onApprovalStatusChange(currentEvent.id, newStatus as 'pending' | 'approved' | 'rejected');
                        }
                      }}
                      options={[
                        { value: 'pending', label: 'Pendiente', color: '#3b82f6' },
                        { value: 'approved', label: 'Aprobado', color: '#10b981' },
                        { value: 'rejected', label: 'Rechazado', color: '#ef4444' }
                      ]}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </div>
      )}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
        }}
        onConfirm={() => {
          onRemove(currentEvent.id);
          setShowDeleteModal(false);
        }}
        confirmText="Aceptar"
        cancelText="Cancelar"
      >
        <p>¿Estás seguro de que deseas eliminar este evento paralelo?</p>
      </Modal>
    </div>
  );
};

const ParallelEventsSection: React.FC<ParallelEventsSectionProps> = ({ parallelEvents, speakers, onChange, onAddSpeaker, readOnly = false, hideTitle = false, initialExpandedEventId, showApprovalBadge = false, onApprovalStatusChange, userId }) => {
  const MAX_EVENTS = 5;
  
  // Estado local que se sincroniza con el padre pero se actualiza inmediatamente
  const [localEvents, setLocalEvents] = useState<ParallelEvent[]>(parallelEvents || []);
  const [newEventIds, setNewEventIds] = useState<Set<string>>(new Set());
  const [newLocalSpeakerIds, setNewLocalSpeakerIds] = useState<Set<string>>(new Set());
  
  // Ref para mantener el estado más reciente de los eventos y evitar condiciones de carrera
  const eventsRef = useRef<ParallelEvent[]>(parallelEvents || []);
  
  // Actualizar el estado local y el ref cuando cambian los eventos del padre
  useEffect(() => {
    const newEvents = parallelEvents || [];
    eventsRef.current = newEvents;
    setLocalEvents(newEvents);
  }, [parallelEvents]);

  // Validar y limpiar los datos de eventos paralelos
  // Usar el estado local que se actualiza inmediatamente
  const validEvents = React.useMemo(() => {
    const sourceEvents = localEvents;
    
    if (!sourceEvents || !Array.isArray(sourceEvents)) {
      return [];
    }
    return sourceEvents.filter(event => {
      // Validar que el evento tenga al menos un id
      return event && event.id;
    }).map(event => {
      // Migrar attendeeProfiles de formato antiguo (string[]) a nuevo (AttendeeProfile[])
      let migratedAttendeeProfiles: AttendeeProfile[] | undefined = undefined;
      if (event.attendeeProfiles) {
        if (Array.isArray(event.attendeeProfiles)) {
          // Verificar si es el formato antiguo (strings) o nuevo (objetos)
          if (event.attendeeProfiles.length > 0 && typeof event.attendeeProfiles[0] === 'string') {
            // Formato antiguo: migrar a nuevo formato intentando inferir la temática
            migratedAttendeeProfiles = (event.attendeeProfiles as unknown as string[]).map(profile => {
              // Intentar encontrar la temática del perfil
              let foundThematicAxis = '';
              Object.keys(attendeeProfilesByThematicAxis).forEach(axisLabel => {
                if (attendeeProfilesByThematicAxis[axisLabel].includes(profile)) {
                  foundThematicAxis = axisLabel;
                }
              });
              
              return {
                profile: profile,
                thematicAxis: foundThematicAxis
              };
            });
          } else {
            // Ya está en el formato nuevo
            migratedAttendeeProfiles = event.attendeeProfiles as AttendeeProfile[];
          }
        }
      }
      
      return {
      ...event,
      id: event.id || Date.now().toString(),
      name: event.name || '',
      description: event.description || '',
        speakerIds: event.speakerIds || (event.speakerId ? [event.speakerId] : []),
        speakerId: event.speakerId || '', // Mantener para compatibilidad
      duration: event.duration || '',
        thematicAxes: event.thematicAxes || [],
        presentationFile: Array.isArray(event.presentationFile) ? event.presentationFile : (event.presentationFile ? [event.presentationFile] : undefined),
        attendeeProfiles: migratedAttendeeProfiles,
        localSpeaker: event.localSpeaker, // Incluir el orador local temporal para que se guarde
        localSpeakers: event.localSpeakers, // Incluir la lista de oradores locales para que se guarde
        requestApproval: event.requestApproval || false
      };
    });
  }, [localEvents]);

  const addEvent = () => {
    if (validEvents.length >= MAX_EVENTS) {
      return;
    }
    const newId = Date.now().toString();
    setNewEventIds(prev => new Set(prev).add(newId));
    const newEvent: ParallelEvent = {
      id: newId,
      name: '',
      description: '',
      speakerIds: [],
      duration: '',
      thematicAxes: [],
      requestApproval: false
    };
    onChange([...validEvents, newEvent]);
  };

  const updateEvent = (id: string, field: keyof ParallelEvent, value: any) => {
    if (field === 'localSpeakers') {
    }
    
    // Obtener el orden actual de los eventos para mantenerlo
    const currentOrder = localEvents.map(e => e.id);
    
    // Actualizar el estado local inmediatamente para que el componente se re-renderice
    setLocalEvents(prevEvents => {
      const updatedEvents = prevEvents.map(e => {
        if (e.id === id) {
          // Si estamos actualizando localSpeakers, asegurarnos de que sea un array
          if (field === 'localSpeakers') {
            const newValue = Array.isArray(value) ? value : (value || []);
            return { ...e, [field]: newValue };
          }
          return { ...e, [field]: value };
        }
        return e;
      });
      // Mantener el orden original
      const orderedEvents = currentOrder.map(orderId => 
        updatedEvents.find(e => e.id === orderId)!
      ).filter(Boolean);
      if (field === 'localSpeakers') {
        const updatedEvent = orderedEvents.find(e => e.id === id);
      }
      // Actualizar el ref ANTES de llamar a onChange
      eventsRef.current = orderedEvents;
      return orderedEvents;
    });
    
    // Llamar a onChange DESPUÉS de actualizar el estado local
    // Usar flushSync para asegurar que el padre se actualice inmediatamente
    flushSync(() => {
      const currentEvents = eventsRef.current;
      const updatedEvents = currentEvents.map(e => {
        if (e.id === id) {
          if (field === 'localSpeakers') {
            const newValue = Array.isArray(value) ? value : (value || []);
            return { ...e, [field]: newValue };
          }
          return { ...e, [field]: value };
        }
        return e;
      });
      // Mantener el orden
      const orderedEvents = currentOrder.map(orderId => 
        updatedEvents.find(e => e.id === orderId)!
      ).filter(Boolean);
      eventsRef.current = orderedEvents;
      onChange(orderedEvents);
    });
  };
  
  // Función para actualizar múltiples campos de un evento a la vez (no se usa actualmente)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const updateEventMultiple = (id: string, updates: Partial<ParallelEvent>) => {
    // Esta función no se usa actualmente
    // Se mantiene para referencia futura
    return;
  };

  const removeEvent = (id: string) => {
    onChange(validEvents.filter(e => e.id !== id));
  };

  return (
    <FormSection 
      title={hideTitle ? undefined : (
        <>
          Eventos Paralelos <span style={{ fontSize: '14px', fontWeight: 400, color: '#64748b' }}>(Sujeto a categoría de participación / Requiere aprobación)</span>
        </>
      )}
    >
      {validEvents.map((event) => {
        // Usar el evento más reciente del ref si está disponible
        const mostRecentEvent = eventsRef.current.find(e => e.id === event.id) || event;
        return (
        <ParallelEventCard
            key={event.id}
            event={mostRecentEvent}
          speakers={speakers}
          onUpdate={updateEvent}
          onRemove={removeEvent}
          onAddSpeaker={onAddSpeaker}
          readOnly={readOnly}
          initialExpanded={newEventIds.has(event.id) || (initialExpandedEventId !== undefined && event.id === initialExpandedEventId)}
          newLocalSpeakerIds={newLocalSpeakerIds}
          onNewLocalSpeaker={(speakerId) => {

            // Usar flushSync para asegurar que el estado se actualice antes del siguiente render
            flushSync(() => {
              setNewLocalSpeakerIds(prev => {
                const newSet = new Set(prev);
                newSet.add(speakerId);

                return newSet;
              });
            });

          }}
          showApprovalBadge={showApprovalBadge}
          onApprovalStatusChange={onApprovalStatusChange}
          userId={userId}
        />
        );
      })}

      {validEvents.length < MAX_EVENTS && !readOnly && (
        <button 
          type="button" 
          className="add-button" 
          onClick={addEvent}
          style={{ marginTop: '0px' }}
        >
          + Agregar Evento Paralelo
        </button>
      )}
      {validEvents.length >= MAX_EVENTS && (
        <p className="max-events-message">
          Se ha alcanzado el máximo de {MAX_EVENTS} eventos paralelos permitidos.
        </p>
      )}
    </FormSection>
  );
};

export default ParallelEventsSection;
